import React, { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  Upload,
  FileText,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Loader,
  X,
  TrendingUp,
  Calculator,
  Activity,
  Info,
  ChevronDown,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  RotateCcw,
  Edit2,
  Calendar,
  FileDown
} from 'lucide-react';
import { getYear } from 'date-fns';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  getSoxCycleName as getSoxCycleNameFromReference,
  getCustomSoxCycleValidationError
} from '../utils/soxCycleReference';
import {
  getAvailableSoxCyclesFromApiData,
  resolveSoxCyclesWithDefaults
} from '../utils/soxCycleDefaults';
import { useSoxDefaultsMappings } from '../hooks/useSoxDefaultsMappings';
import useAuthUser from '../hooks/useAuthUser';
import { MODULE_UPLOAD } from '../utils/moduleUpload.ts';
import { uploadFilesAndComplete, initUpload, uploadToGCS, completeUpload } from '../utils/fileUpload';
import {
  MATERIALITY_ASSESSMENT_STORAGE_KEY,
  getUploadSlotLabel,
  getAnalyzeDocumentsSlotActions,
  mergedEntryFromReuseSlot,
  entriesForAnalyzeDocumentsRequest,
  isRealUploadFile,
  pruneMaterialityParallelArrays,
  hasDocumentAnalysisCache,
  resolveUploadedFileEntriesForAnalyze,
  remapIndicesAfterRemoval,
  alignAndExtendParallelArray,
  analyzeSlotActionsByIndex,
  entryHasServerFileId,
} from '../utils/materialityAssessmentState';
import { useMaterialityLeaveGuard } from '../contexts/MaterialityLeaveGuardContext.tsx';
import { ConfirmDialogModal } from '../shared/common/ConfirmDialogModal.tsx';
import {
  BALANCE_SHEET_LINE_CATEGORIES,
  INCOME_STATEMENT_LINE_CATEGORIES,
  normalizeBalanceSheetItemCategory,
  normalizeIncomeStatementItemCategory,
} from '../utils/balanceSheetLineCategory';
import {
  CLIENT_LINE_ITEM_UUID_KEY,
  ensureClientLineItemUuidOnItem,
  ensureClientUuidsOnLineItemsObject,
  migrateLineItemNotesToClientKeys,
  serializeLineItemNotesForApi,
  stripClientLineItemUuidDeep,
  stripClientLineItemUuidFromItem,
} from '../utils/clientLineItemIdentity';
import cloneDeep from 'lodash/cloneDeep';
import { Select } from './common';
import {
  MATERIALITY_FISCAL_YEAR_PREVIOUS_YEARS,
  MATERIALITY_FISCAL_YEAR_FUTURE_YEARS,
} from '../constants/materiality';
import { useAuditYears } from '../hooks/useAuditYears.ts';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import toast from 'react-hot-toast';

const AnalyzeDocuments = lazy(() => import('./AnalyzeDocuments'));
const MaterialityAnalysis = lazy(() => import('./MaterialityAnalysis'));
const RiskAssessment = lazy(() => import('./RiskAssessment'));
const SOXCycleMapping = lazy(() => import('./SOXCycleMapping'));
const EntityScoping = lazy(() => import('./EntityScoping'));

function cloneMaterialityRiskScores(scores) {
  if (scores == null) return null;
  return cloneDeep(scores);
}

/** Multi-doc: riskScores keyed by index; single-doc: flat object. Use isMultiDoc from allLineItemData.length > 1. */
function getRiskScoresForActiveDocument(riskScores, activeLineItemIndex, isMultiDoc) {
  if (!riskScores) return null;
  if (isMultiDoc) {
    const doc = riskScores[activeLineItemIndex];
    return doc ?? null;
  }
  return riskScores;
}

function normalizeRiskLabelForMerge(label) {
  return String(label ?? '').toLowerCase().trim();
}

/**
 * Find a risk-assessment row for a line item (nested or flat API shape).
 * Used when saving: merge current lineItemData labels/values with stored risk metadata.
 */
function findRiskEntryForLineItem(statementData, statementType, label) {
  if (!statementData || label == null) return null;
  const searchLabel = normalizeRiskLabelForMerge(label);
  const rowLabel = (item) => normalizeRiskLabelForMerge(item.label || item.line_item || '');

  if (statementData.items && Array.isArray(statementData.items)) {
    const hit = statementData.items.find((item) => rowLabel(item) === searchLabel);
    if (hit) return hit;
  }

  if (statementType === 'balance_sheet') {
    if (statementData.assets?.current_assets) {
      const hit = statementData.assets.current_assets.find((item) => rowLabel(item) === searchLabel);
      if (hit) return hit;
    }
    if (statementData.assets?.non_current_assets) {
      const hit = statementData.assets.non_current_assets.find((item) => rowLabel(item) === searchLabel);
      if (hit) return hit;
    }
    if (statementData.liabilities?.current_liabilities) {
      const hit = statementData.liabilities.current_liabilities.find((item) => rowLabel(item) === searchLabel);
      if (hit) return hit;
    }
    if (statementData.liabilities?.non_current_liabilities) {
      const hit = statementData.liabilities.non_current_liabilities.find((item) => rowLabel(item) === searchLabel);
      if (hit) return hit;
    }
    if (statementData.equity) {
      const equityItems = statementData.equity.items || statementData.equity;
      if (Array.isArray(equityItems)) {
        const hit = equityItems.find((item) => rowLabel(item) === searchLabel);
        if (hit) return hit;
      }
    }
  } else if (statementType === 'income_statement') {
    if (statementData.revenue) {
      const hit = statementData.revenue.find((item) => rowLabel(item) === searchLabel);
      if (hit) return hit;
    }
    if (statementData.expenses) {
      const hit = statementData.expenses.find((item) => rowLabel(item) === searchLabel);
      if (hit) return hit;
    }
  }

  return null;
}

function pickRiskLayerForIndexedRow(statementData, statementType, lineItem, index) {
  if (!statementData) return null;
  const flat = statementData.items;
  const clientUid = lineItem?.[CLIENT_LINE_ITEM_UUID_KEY];
  // 1) Match by client UUID when risk rows were stamped (stable across renames/reorders).
  if (Array.isArray(flat) && clientUid) {
    const byUuid = flat.find((row) => row[CLIENT_LINE_ITEM_UUID_KEY] === clientUid);
    if (byUuid) return byUuid;
  }
  // 2) Same index in flat v2 arrays (labels may differ after edits).
  if (Array.isArray(flat) && index >= 0 && index < flat.length) {
    const byIdx = flat[index];
    if (byIdx) return byIdx;
  }
  return findRiskEntryForLineItem(
    statementData,
    statementType,
    lineItem.label || lineItem.line_item || ''
  );
}

/** Copy `__clientLineItemUuid` from UI line items onto risk `items` rows at the same index (in-memory only). */
function syncClientUuidsFromLineItemsOntoRiskDoc(lineItemsBundle, riskScoresDoc) {
  if (!lineItemsBundle || !riskScoresDoc || typeof riskScoresDoc !== 'object') return;
  ['balance_sheet', 'income_statement'].forEach((stm) => {
    const liItems = lineItemsBundle[stm]?.items;
    const riskItems = riskScoresDoc[stm]?.items;
    if (!Array.isArray(liItems) || !Array.isArray(riskItems)) return;
    const n = Math.min(liItems.length, riskItems.length);
    for (let i = 0; i < n; i++) {
      const uid = liItems[i][CLIENT_LINE_ITEM_UUID_KEY];
      if (uid) riskItems[i][CLIENT_LINE_ITEM_UUID_KEY] = uid;
    }
  });
}

function syncClientUuidsFromLineItemsOntoRiskScoresRoot(allBundles, scoresRoot) {
  if (!Array.isArray(allBundles) || !allBundles.length || !scoresRoot) return;
  const multi = allBundles.length > 1;
  allBundles.forEach((bundle) => {
    const lineItems = bundle.lineItems;
    if (!lineItems) return;
    const riskDoc = multi ? scoresRoot[bundle.documentIndex] : scoresRoot;
    if (riskDoc) syncClientUuidsFromLineItemsOntoRiskDoc(lineItems, riskDoc);
  });
}

/**
 * Build riskScores payload from UI line items so edited/added rows match what the user sees.
 * Without this, save sends stale riskScores from the assess-risks API while the UI uses lineItemData.
 */
function buildMergedRiskScoresForSave(
  lineItemData,
  activeRiskScores,
  activeLineItemIndex,
  lineItemNotes,
  annualizationFactor = 1,
  resolveCurrentSoxCycles = null
) {
  const out = activeRiskScores ? cloneDeep(activeRiskScores) : {};
  const notes = lineItemNotes || {};
  const docIdx = activeLineItemIndex;

  const mergeBs = () => {
    const bsLine = lineItemData?.balance_sheet?.items;
    if (!Array.isArray(bsLine)) return;
    if (!out.balance_sheet) out.balance_sheet = {};
    const rsBs = activeRiskScores?.balance_sheet;
    if (bsLine.length === 0) {
      out.balance_sheet.items = [];
      return;
    }
    const merged = bsLine.map((li, idx) => {
      const fromRisk = pickRiskLayerForIndexedRow(rsBs, 'balance_sheet', li, idx);
      const clientUid = li[CLIENT_LINE_ITEM_UUID_KEY];
      const legacyNoteKey = `bs_${docIdx}_${idx}`;
      const noteVal =
        clientUid && Object.prototype.hasOwnProperty.call(notes, clientUid)
          ? notes[clientUid]
          : Object.prototype.hasOwnProperty.call(notes, legacyNoteKey)
            ? notes[legacyNoteKey]
            : '';
      const riskScore =
        li.risk_score != null && li.risk_score !== ''
          ? li.risk_score
          : fromRisk?.risk_score ?? fromRisk?.corporate_risk_score;
      const riskCategory =
        li.risk_category || fromRisk?.risk_category || fromRisk?.risk_level;
      // For riskScores payload, use the current UI row mapping only.
      // Do not backfill from stale risk rows to avoid partial/misaligned SOX cycles.
      let resolvedSoxCycles;
      if (Array.isArray(li.sox_cycles)) {
        resolvedSoxCycles = li.sox_cycles;
      } else if (li.sox_cycle) {
        resolvedSoxCycles = [li.sox_cycle];
      } else if (typeof resolveCurrentSoxCycles === 'function') {
        resolvedSoxCycles = resolveCurrentSoxCycles(li, 'balance_sheet');
      } else {
        resolvedSoxCycles = [];
      }
      return stripClientLineItemUuidFromItem({
        ...(fromRisk || {}),
        ...li,
        label: li.label,
        value: li.value,
        category: li.category,
        units: li.units || 'USD',
        risk_score: riskScore,
        risk_category: riskCategory,
        sox_cycles: resolvedSoxCycles,
        sox_cycle: li.sox_cycle ?? resolvedSoxCycles?.[0] ?? null,
        risk_factors: li.risk_factors ?? fromRisk?.risk_factors,
        rationale: li.rationale ?? fromRisk?.rationale,
        notes: noteVal
      });
    });
    out.balance_sheet.items = merged;
  };

  const mergeIs = () => {
    const isLine = lineItemData?.income_statement?.items;
    if (!Array.isArray(isLine)) return;
    if (!out.income_statement) out.income_statement = {};
    const rsIs = activeRiskScores?.income_statement;
    if (isLine.length === 0) {
      out.income_statement.items = [];
      return;
    }
    const pickIsNote = (idx, li) => {
      const clientUid = li[CLIENT_LINE_ITEM_UUID_KEY];
      if (clientUid && Object.prototype.hasOwnProperty.call(notes, clientUid)) {
        return notes[clientUid];
      }
      const stableKey = `is_${docIdx}_${idx}`;
      const itemType = li.category === 'revenue' ? 'is_revenue' : 'is_expense';
      const typedKey = `${itemType}_${docIdx}_${idx}`;
      const legKey = `income_statement_${docIdx}_${idx}`;
      const pick = (k) => (Object.prototype.hasOwnProperty.call(notes, k) ? notes[k] : undefined);
      return pick(stableKey) ?? pick(typedKey) ?? pick(legKey) ?? '';
    };

    const merged = isLine.map((li, idx) => {
      const fromRisk = pickRiskLayerForIndexedRow(rsIs, 'income_statement', li, idx);
      const noteVal = pickIsNote(idx, li);
      const riskScore =
        li.risk_score != null && li.risk_score !== ''
          ? li.risk_score
          : fromRisk?.risk_score ?? fromRisk?.corporate_risk_score;
      const riskCategory =
        li.risk_category || fromRisk?.risk_category || fromRisk?.risk_level;
      const baseLabel = li.label || fromRisk?.label || fromRisk?.line_item || '';
      const uiDisplayValue = (Number(li.value) || 0) * annualizationFactor;
      // For riskScores payload, use the current UI row mapping only.
      // Do not backfill from stale risk rows to avoid partial/misaligned SOX cycles.
      let resolvedSoxCycles;
      if (Array.isArray(li.sox_cycles)) {
        resolvedSoxCycles = li.sox_cycles;
      } else if (li.sox_cycle) {
        resolvedSoxCycles = [li.sox_cycle];
      } else if (typeof resolveCurrentSoxCycles === 'function') {
        resolvedSoxCycles = resolveCurrentSoxCycles(li, 'income_statement');
      } else {
        resolvedSoxCycles = [];
      }
      return stripClientLineItemUuidFromItem({
        ...(fromRisk || {}),
        ...li,
        label: baseLabel,
        line_item: baseLabel,
        value: uiDisplayValue,
        category: li.category,
        units: li.units || 'USD',
        risk_score: riskScore,
        risk_category: riskCategory,
        sox_cycles: resolvedSoxCycles,
        sox_cycle: li.sox_cycle ?? resolvedSoxCycles?.[0] ?? null,
        risk_factors: li.risk_factors ?? fromRisk?.risk_factors,
        rationale: li.rationale ?? fromRisk?.rationale,
        notes: noteVal
      });
    });
    out.income_statement.items = merged;
  };

  mergeBs();
  mergeIs();
  return Object.keys(out).length > 0 ? out : null;
}

/** Same non-empty criteria as risk assessment uses for BS / IS items. */
function materialityLineItemsLookExtracted(lineItems) {
  if (!lineItems || typeof lineItems !== 'object') return false;
  const bsItems = lineItems.balance_sheet?.items;
  const isItems = lineItems.income_statement?.items;
  const hasBs = Array.isArray(bsItems) && bsItems.length > 0;
  const hasIs = Array.isArray(isItems) && isItems.length > 0;
  return hasBs || hasIs;
}

function lineItemBundleMatchesSlot(bundle, docIndex, fileId, doc) {
  if (!bundle || bundle.documentIndex !== docIndex || !materialityLineItemsLookExtracted(bundle.lineItems)) {
    return false;
  }
  if (fileId && bundle.fileId && bundle.fileId === fileId) return true;
  if (fileId && !bundle.fileId && doc && bundle.filename === doc.filename) return true;
  return false;
}

function parseSoxItemId(itemId) {
  const m = /^(bs_asset|bs_liability|bs_equity|is_revenue|is_expense)_(\d+)_(.+)$/.exec(
    String(itemId ?? '')
  );
  if (!m) return null;
  return {
    itemType: m[1],
    rowIndex: Number(m[2]),
    slug: m[3]
  };
}

function resolveBalanceSheetFlatIndexForSoxId(bsItems, parsed) {
  if (!Array.isArray(bsItems)) return -1;
  const categoryByType = {
    bs_asset: 'asset',
    bs_liability: 'liability',
    bs_equity: 'equity'
  };
  const targetCategory = categoryByType[parsed.itemType];
  if (!targetCategory) return -1;
  const notTotal = (item) => !(String(item?.label ?? '').toLowerCase().includes('total'));
  const typedRows = bsItems
    .map((item, flatIdx) => ({ item, flatIdx }))
    .filter(({ item }) => {
      const itemCategory = normalizeBalanceSheetItemCategory(item);
      return notTotal(item) && itemCategory === targetCategory;
    });
  const hit = typedRows[parsed.rowIndex];
  return hit ? hit.flatIdx : -1;
}

function updateLineItemSoxCyclesByItemId(lineItems, itemId, soxCycles) {
  const parsed = parseSoxItemId(itemId);
  if (!parsed || !lineItems) return lineItems;
  const nextCycles = Array.isArray(soxCycles) ? [...soxCycles] : [];
  const primaryCycle = nextCycles[0] ?? null;

  if (parsed.itemType.startsWith('bs_')) {
    const bsItems = lineItems?.balance_sheet?.items;
    const flatIdx = resolveBalanceSheetFlatIndexForSoxId(bsItems, parsed);
    if (flatIdx < 0 || !bsItems[flatIdx]) return lineItems;
    return {
      ...lineItems,
      balance_sheet: {
        ...lineItems.balance_sheet,
        items: bsItems.map((item, idx) =>
          idx === flatIdx ? { ...item, sox_cycles: nextCycles, sox_cycle: primaryCycle } : item
        )
      }
    };
  }

  if (parsed.itemType.startsWith('is_')) {
    const isItems = lineItems?.income_statement?.items;
    if (!Array.isArray(isItems) || !isItems[parsed.rowIndex]) return lineItems;
    return {
      ...lineItems,
      income_statement: {
        ...lineItems.income_statement,
        items: isItems.map((item, idx) =>
          idx === parsed.rowIndex ? { ...item, sox_cycles: nextCycles, sox_cycle: primaryCycle } : item
        )
      }
    };
  }

  return lineItems;
}

function getLineItemBySoxItemId(lineItems, itemId) {
  const parsed = parseSoxItemId(itemId);
  if (!parsed || !lineItems) return null;
  if (parsed.itemType.startsWith('bs_')) {
    const bsItems = lineItems?.balance_sheet?.items;
    const flatIdx = resolveBalanceSheetFlatIndexForSoxId(bsItems, parsed);
    return Array.isArray(bsItems) && flatIdx >= 0 ? bsItems[flatIdx] || null : null;
  }
  if (parsed.itemType.startsWith('is_')) {
    const isItems = lineItems?.income_statement?.items;
    return Array.isArray(isItems) ? isItems[parsed.rowIndex] || null : null;
  }
  return null;
}

function Materiality({ user }) {
  const { data: auditYearsData } = useAuditYears();
  const { selectedAuditYearId } = useAuditYear();
  const [currentStep, setCurrentStep] = useState(1); // 1-6 for the steps (6 is confirmation)
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedFileEntries, setUploadedFileEntries] = useState([]);
  const [documentAnalysisResults, setDocumentAnalysisResults] = useState([]);
  const [selectedDocumentIndices, setSelectedDocumentIndices] = useState([]); // Changed to array for multiple selections
  const [riskAssessmentDocIndices, setRiskAssessmentDocIndices] = useState([]); // For risk assessment column
  const [selectedAnalysisYear, setSelectedAnalysisYear] = useState('');
  const [lastAnalyzedFiscalYear, setLastAnalyzedFiscalYear] = useState('');
  const [showDifferentYearWarning, setShowDifferentYearWarning] = useState(false);
  const [detectedMismatchYear, setDetectedMismatchYear] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null); // { message, code }
  const [uploadProgress, setUploadProgress] = useState(0);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Net Income');
  const [materialityRecommendation, setMaterialityRecommendation] = useState(null);
  const [recommendationReasoning, setRecommendationReasoning] = useState('');
  const [recommendationConfidence, setRecommendationConfidence] = useState('medium');
  const [sensitivityLevel, setSensitivityLevel] = useState('Standard');
  const [overrideAmount, setOverrideAmount] = useState('');
  const [overrideReasoning, setOverrideReasoning] = useState('');
  const [customOverallMateriality, setCustomOverallMateriality] = useState('');
  const [customPlanningMateriality, setCustomPlanningMateriality] = useState('');
  const [customDeminimis, setCustomDeminimis] = useState('');
  const [showReasoningBox, setShowReasoningBox] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisSteps, setAnalysisSteps] = useState([
    { name: 'Analyzing Industry Vertical', status: 'pending' },
    { name: 'Processing Financial Metrics', status: 'pending' },
    { name: 'Assessing Risk Profile', status: 'pending' },
    { name: 'Determining Materiality Category', status: 'pending' }
  ]);
  const [expandedSections, setExpandedSections] = useState({
    balanceSheet: true,
    assets: true,
    currentAssets: true,
    nonCurrentAssets: true,
    liabilities: true,
    currentLiabilities: true,
    nonCurrentLiabilities: true,
    equity: true,
    incomeStatement: true,
    incomeRevenue: true,
    incomeExpense: true
  });
  const [editingItem, setEditingItem] = useState(null);
  const {
    data: soxDefaultsData,
    isLoading: soxDefaultsLoading,
    isError: soxDefaultsError,
    refetch: retrySoxDefaults
  } = useSoxDefaultsMappings(currentStep === 5);
  const soxDefaultsMappings = soxDefaultsData?.mappings || null;
  const availableSoxCycles = useMemo(
    () => getAvailableSoxCyclesFromApiData(soxDefaultsData),
    [soxDefaultsData]
  );
  const [activeDocumentIndex, setActiveDocumentIndex] = useState(null); // Track which document is currently displayed
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false); // For animation
  const [allDocumentResponses, setAllDocumentResponses] = useState([]); // Store all document responses
  const [lineItemData, setLineItemData] = useState(null);
  const [isLoadingLineItems, setIsLoadingLineItems] = useState(false);
  const [confirmedMateriality, setConfirmedMateriality] = useState(null);
  const [allLineItemData, setAllLineItemData] = useState([]); // Store line items from multiple documents
  const [activeLineItemIndex, setActiveLineItemIndex] = useState(0); // Track which document's line items to display
  /** Latest doc tab index for async fetch (re-entering Risk Assessment must not reset to first doc). */
  const activeLineItemIndexRef = useRef(0);
  useEffect(() => {
    activeLineItemIndexRef.current = activeLineItemIndex;
  }, [activeLineItemIndex]);
  const [riskScores, setRiskScores] = useState(null);
  const [customRiskLevels, setCustomRiskLevels] = useState({});
  const [isCalculatingRisk, setIsCalculatingRisk] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [isSavingAssessment, setIsSavingAssessment] = useState(false);
  const [entityScopingDataForSave, setEntityScopingDataForSave] = useState(null);
  const [riskAnalysisProgress, setRiskAnalysisProgress] = useState('');
  const [editingRiskScore, setEditingRiskScore] = useState(null);
  const [trialBalanceFile, setTrialBalanceFile] = useState(null);
  const [isUploadingTrialBalance, setIsUploadingTrialBalance] = useState(false);
  const [showScopingMatrix, setShowScopingMatrix] = useState(false);
  const [scopingFilter, setScopingFilter] = useState('all'); // 'all', 'in-scope', 'out-of-scope'
  const [thresholdOverride, setThresholdOverride] = useState('');
  const [thresholdJustification, setThresholdJustification] = useState('');
  const [expandedGLCategories, setExpandedGLCategories] = useState({});
  const [showThresholdOverride, setShowThresholdOverride] = useState(false);
  const [glEntriesData, setGlEntriesData] = useState(null);
  const [isAnalyzingTrialBalance, setIsAnalyzingTrialBalance] = useState(false);
  const [trialBalanceDialog, setTrialBalanceDialog] = useState({
    open: false,
    title: '',
    subtitle: '',
    message: '',
    variant: 'danger',
  });
  // Entity scoping report state (lifted up so the unified outer footer can
  // drive Generate / Show Report / Complete Risk Assessment from one place)
  const [showScopingReport, setShowScopingReport] = useState(false);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const [entityScopingData, setEntityScopingData] = useState([]);
  const [entityPeriod, setEntityPeriod] = useState('');
  const [entitiesAnalyzeError, setEntitiesAnalyzeError] = useState(null);
  const [entityAnalyzeDialog, setEntityAnalyzeDialog] = useState({
    open: false,
    title: '',
    subtitle: '',
    message: '',
    variant: 'warning',
  });
  const ENTITY_DATA_NOT_FOUND_MESSAGE =
    'No entity data was found for this trial balance. Check the file format and entity mapping, then try again.';
  const [showAddLineItemForm, setShowAddLineItemForm] = useState({ show: false, statementType: null, category: null });
  const [newLineItem, setNewLineItem] = useState({
    label: '',
    value: '',
    riskScore: '',
    note: '',
    balanceSheetCategory: 'asset',
    incomeStatementCategory: 'revenue'
  });
  const [customSoxCycles, setCustomSoxCycles] = useState([]);
  const [showAddSoxCycleForm, setShowAddSoxCycleForm] = useState(false);
  const [newSoxCycle, setNewSoxCycle] = useState({ code: '', description: '' });
  const [lineItemNotes, setLineItemNotes] = useState({}); // Store notes for each line item
  const [editingNoteKey, setEditingNoteKey] = useState(null); // Track which note is being edited
  const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
  const MAX_FILES = 5;

  const { setMaterialityHasUnsavedProgress } = useMaterialityLeaveGuard();
  useEffect(() => {
    const leaveGuardSuppressed =
      currentStep >= 8 || (currentStep === 7 && trialBalanceFile != null);
    setMaterialityHasUnsavedProgress(
      uploadedFiles.length > 0 && !leaveGuardSuppressed
    );
    return () => setMaterialityHasUnsavedProgress(false);
  }, [
    uploadedFiles.length,
    currentStep,
    trialBalanceFile,
    setMaterialityHasUnsavedProgress,
  ]);

  /** Clears materiality/risk state after file list changes; does not clear upload slots or server file_id rows. */
  const resetDownstreamAfterUploadListChange = useCallback(() => {
    setSelectedDocumentIndices([]);
    setRiskAssessmentDocIndices([]);
    setLastAnalyzedFiscalYear('');
    setShowDifferentYearWarning(false);
    setDetectedMismatchYear('');
    setCompanyProfile(null);
    setDocumentInfo(null);
    setAllDocumentResponses([]);
    setLineItemData(null);
    setAllLineItemData([]);
    setRiskScores(null);
    setActiveDocumentIndex(null);
    setActiveLineItemIndex(0);
    setConfirmedMateriality(null);
    setLineItemNotes({});
  }, []);

  const handleAddMoreFilesChange = useCallback(
    (e) => {
      const newFiles = Array.from(e.target.files || []);

      if (newFiles.length === 0) {
        e.target.value = '';
        return;
      }

      const totalFilesCount = uploadedFiles.length + newFiles.length;
      if (totalFilesCount > MAX_FILES) {
        toast.error(`You can upload a maximum of ${MAX_FILES} files in total.`);
        e.target.value = '';
        return;
      }

      const oversizedFiles = newFiles.filter(
        (file) => file.size > MAX_FILE_SIZE_BYTES
      );
      if (oversizedFiles.length > 0) {
        toast.error(
          'File cannot be more than 50 MB.'
        );
        e.target.value = '';
        return;
      }

      const prevLen = uploadedFiles.length;
      const addCount = newFiles.length;
      setUploadedFiles((prev) => [...prev, ...newFiles]);
      setUploadedFileEntries((prev) =>
        alignAndExtendParallelArray(prev, prevLen, addCount)
      );
      setDocumentAnalysisResults((prev) =>
        alignAndExtendParallelArray(prev, prevLen, addCount)
      );
      resetDownstreamAfterUploadListChange();
      e.target.value = '';
    },
    [
      uploadedFiles,
      MAX_FILE_SIZE_BYTES,
      MAX_FILES,
      resetDownstreamAfterUploadListChange,
    ]
  );
  const fileInputRef = useRef(null);
  const trialBalanceInputRef = useRef(null);
  const materialityPrevStepRef = useRef(null);
  const analyzeDocumentsInFlightRef = useRef(false);
  const allLineItemDataRef = useRef([]);

  useEffect(() => {
    allLineItemDataRef.current = allLineItemData;
  }, [allLineItemData]);

  /**
   * Step 4: load line items via extract-line-items-v2 when missing; reuse allLineItemData when
   * the same slot (documentIndex + file_id) already has extracted BS/IS items.
   */
  const fetchLineItemsForRiskStep = useCallback(async () => {
    const documentsToProcess =
      riskAssessmentDocIndices.length > 0
        ? riskAssessmentDocIndices
        : uploadedFiles.length === 1
          ? [0]
          : [];

    if (documentsToProcess.length === 0) {
      setIsLoadingLineItems(false);
      return;
    }

    setIsLoadingLineItems(true);
    const extractedLineItems = [];
    const cachedBundles = allLineItemDataRef.current;

    try {
      await documentsToProcess.reduce(async (chain, docIndex) => {
        await chain;
        const doc = documentAnalysisResults[docIndex];
        const uploadedEntry = uploadedFileEntries[docIndex];
        const fileId = uploadedEntry?.file_id || uploadedEntry?.uuid;

        if (!doc || !fileId) return;

        const cached = cachedBundles.find((b) => lineItemBundleMatchesSlot(b, docIndex, fileId, doc));
        if (cached) {
          extractedLineItems.push({
            ...cached,
            fileId: cached.fileId || fileId,
            lineItems: ensureClientUuidsOnLineItemsObject(cached.lineItems),
          });
          return;
        }

        const formData = new FormData();
        formData.append('file_id', fileId);
        formData.append('document_category', doc.category);
        formData.append('document_period', doc.period || '');
        formData.append('fiscalYear', selectedAnalysisYear);

        if (doc.contains && Array.isArray(doc.contains)) {
          formData.append('sheet_names', JSON.stringify(doc.contains));
        } else {
          formData.append('sheet_names', JSON.stringify([]));
        }

        const response = await axiosInstance.post('/api/materiality/extract-line-items-v2', formData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.data.success) {

          const lineItems = response.data.line_items;
          if (lineItems?.income_statement?.items) {
            lineItems.income_statement.items = lineItems.income_statement.items.map(item => {
              const u = (item.units || '').toLowerCase();
              let usdValue = item.value;
              if (u.includes('million')) {
                usdValue = item.value * 1000000;
              } else if (u.includes('thousand')) {
                usdValue = item.value * 1000;
              }
              return { ...item, value: usdValue, units: 'USD' };
            });
          }
          if (lineItems?.balance_sheet?.items) {
            lineItems.balance_sheet.items = lineItems.balance_sheet.items.map(item => {
              const u = (item.units || '').toLowerCase();
              let usdValue = item.value;
              if (u.includes('million')) {
                usdValue = item.value * 1000000;
              } else if (u.includes('thousand')) {
                usdValue = item.value * 1000;
              }
              return { ...item, value: usdValue, units: 'USD' };
            });
          }

          extractedLineItems.push({
            filename: doc.filename,
            category: doc.category,
            period: doc.period,
            lineItems: ensureClientUuidsOnLineItemsObject(lineItems),
            documentIndex: docIndex,
            annualizationFactor: lineItems.annualization_factor,
            periodMonths: lineItems.period_months,
            fileId,
          });
        } else {
          console.error(`Failed to extract line items from ${doc.filename}`);
        }
      }, Promise.resolve());

      setAllLineItemData(extractedLineItems);

      if (extractedLineItems.length > 0) {
        const prevIdx = activeLineItemIndexRef.current;
        const safeIdx =
          typeof prevIdx === 'number' && prevIdx >= 0 && prevIdx < extractedLineItems.length
            ? prevIdx
            : 0;
        setLineItemData(extractedLineItems[safeIdx].lineItems);
        setActiveLineItemIndex(safeIdx);
        setLineItemNotes((prev) =>
          extractedLineItems.reduce(
            (acc, bundle, docIdx) => migrateLineItemNotesToClientKeys(acc, bundle.lineItems, docIdx),
            prev
          )
        );
      } else {
        setLineItemData(null);
      }
    } catch (error) {
      console.error('Error fetching line items:', error);
      toast.error('Failed to extract line items. Please try again.');
    } finally {
      setIsLoadingLineItems(false);
    }
  }, [
    riskAssessmentDocIndices,
    documentAnalysisResults,
    uploadedFileEntries,
    uploadedFiles.length,
    selectedAnalysisYear,
    setAllLineItemData,
    setLineItemData,
    setActiveLineItemIndex,
    setIsLoadingLineItems,
    setLineItemNotes,
  ]);

  useEffect(() => {
    if (currentStep !== 4 || uploadedFiles.length === 0) return;

    const documentsToProcess =
      riskAssessmentDocIndices.length > 0
        ? riskAssessmentDocIndices
        : uploadedFiles.length === 1
          ? [0]
          : [];

    if (documentsToProcess.length === 0) {
      setLineItemData(null);
      setAllLineItemData([]);
      setIsLoadingLineItems(false);
      return;
    }

    fetchLineItemsForRiskStep();
  }, [
    currentStep,
    uploadedFiles.length,
    riskAssessmentDocIndices,
    fetchLineItemsForRiskStep,
    setLineItemData,
    setAllLineItemData,
    setIsLoadingLineItems,
  ]);

  useEffect(() => {
    if (!selectedAnalysisYear) {
      setShowDifferentYearWarning(false);
      setDetectedMismatchYear('');
      return;
    }
    if (selectedAnalysisYear !== lastAnalyzedFiscalYear) {
      setShowDifferentYearWarning(false);
      setDetectedMismatchYear('');
    }
  }, [selectedAnalysisYear, lastAnalyzedFiscalYear]);

  const fiscalYearOptions = useMemo(() => {
    // Use audit years from the API when available; fall back to a generated range.
    if (auditYearsData && auditYearsData.length > 0) {
      const apiOptions = auditYearsData.map((ay) => ({
        label: ay.name,
        value: `${ay.year}`,
      }));
      return [{ label: 'Select year', value: '' }, ...apiOptions];
    }
    const currentYear = getYear(new Date());
    const previousYears = Math.max(0, Number(MATERIALITY_FISCAL_YEAR_PREVIOUS_YEARS) || 0);
    const futureYears = Math.max(0, Number(MATERIALITY_FISCAL_YEAR_FUTURE_YEARS) || 0);

    const yearsInRange = Array.from(
      { length: previousYears + futureYears + 1 },
      (_, idx) => currentYear + futureYears - idx
    ).map((year) => ({
      label: `${year}`,
      value: `${year}`,
    }));

    return [{ label: 'Select year', value: '' }, ...yearsInRange];
  }, [auditYearsData]);

  const steps = [
    { id: 1, name: 'Select Fiscal Year', icon: Calendar },
    { id: 2, name: 'Upload Financials', icon: Upload },
    { id: 3, name: 'Analyze Documents', icon: FileText },
    { id: 4, name: 'Materiality Analysis', icon: Calculator },
    { id: 5, name: 'Risk Assessment', icon: TrendingUp },
    { id: 6, name: 'SOX Cycle Mapping', icon: Activity },
    { id: 7, name: 'Entity Scoping', icon: FileText },
    { id: 8, name: 'Complete', icon: CheckCircle, hidden: true } // Hidden from progress bar
  ];

  // Console log when Risk Assessment documents are selected
  useEffect(() => {
    if (riskAssessmentDocIndices.length > 0) {
      const selectedDocs = riskAssessmentDocIndices.map(idx => documentAnalysisResults[idx]?.filename || `Document ${idx}`);
      console.log('Risk Assessment - Selected Documents:', selectedDocs);
      console.log('Risk Assessment - Document Indices:', riskAssessmentDocIndices);
    }
  }, [riskAssessmentDocIndices, documentAnalysisResults]);

  // Update materiality recommendation based on selected documents
  useEffect(() => {
    if (selectedDocumentIndices.length === 0 || !companyProfile) {
      return;
    }

    // Check if company is showing losses (negative net income)
    const isShowingLosses = companyProfile.netIncome < 0;

    if (isShowingLosses) {
      setMaterialityRecommendation('Total Revenue');
      setRecommendationReasoning(`Company showing losses (Net Income: ${formatCurrency(companyProfile.netIncome)}) - Total Revenue is the appropriate materiality basis`);
    } else {
      setMaterialityRecommendation('Net Income');
      setRecommendationReasoning(`Company showing profits (Net Income: ${formatCurrency(companyProfile.netIncome)}) - Net Income (Pre-Tax) is the appropriate materiality basis`);
    }
  }, [selectedDocumentIndices, companyProfile]);

  // Returning to upload step: drop placeholder rows with no file_id
  useEffect(() => {
    const prev = materialityPrevStepRef.current;
    materialityPrevStepRef.current = currentStep;

    if (currentStep !== 2) return;
    if (prev === null) return;
    if (prev === 2) return;

    const pruned = pruneMaterialityParallelArrays({
      uploadSlots: uploadedFiles,
      uploadedFileEntries,
      documentAnalysisResults,
      selectedDocumentIndices,
      riskAssessmentDocIndices,
    });

    if (!pruned.changed) return;

    setUploadedFiles(pruned.uploadSlots);
    setUploadedFileEntries(pruned.uploadedFileEntries);
    setDocumentAnalysisResults(pruned.documentAnalysisResults);
    setSelectedDocumentIndices(pruned.selectedDocumentIndices);
    setRiskAssessmentDocIndices(pruned.riskAssessmentDocIndices);
  }, [
    currentStep,
    uploadedFiles,
    uploadedFileEntries,
    documentAnalysisResults,
    selectedDocumentIndices,
    riskAssessmentDocIndices,
  ]);

  // Sensitivity multipliers - now with specific percentages per category
  const getSensitivityOptions = (category) => {
    if (category === 'Net Income') {
      return [
        { label: 'Conservative', value: 'conservative', percentage: '2.5%' },
        { label: 'Standard', value: 'standard', percentage: '5.0%', selected: true },
        { label: 'Moderate', value: 'moderate', percentage: '7.5%' }
      ];
    } else if (category === 'Total Revenue') {
      return [
        { label: 'Conservative', value: 'conservative', percentage: '0.5%' },
        { label: 'Standard', value: 'standard', percentage: '1.0%', selected: true },
        { label: 'Moderate', value: 'moderate', percentage: '2.0%' }
      ];
    } else if (category === 'Total Assets') {
      return [
        { label: 'Conservative', value: 'conservative', percentage: '0.5%' },
        { label: 'Standard', value: 'standard', percentage: '1.0%', selected: true },
        { label: 'Moderate', value: 'moderate', percentage: '2.0%' }
      ];
    }
    // Default fallback
    return [
      { label: 'Conservative', value: 'conservative', percentage: '2.5%' },
      { label: 'Standard', value: 'standard', percentage: '5.0%', selected: true },
      { label: 'Moderate', value: 'moderate', percentage: '7.5%' }
    ];
  };
  // Get SOX cycles for a given line item
  const getSoxCycles = useCallback((itemLabel, item = null, statementType = 'balance_sheet') => {
    if (Array.isArray(item?.sox_cycles) && item.sox_cycles.length > 0) {
      return item.sox_cycles;
    }
    return resolveSoxCyclesWithDefaults({
      mappings: soxDefaultsMappings,
      itemLabel,
    });
  }, [soxDefaultsMappings]);

  // Handle SOX cycle selection
  const handleSoxCycleToggle = (itemId, cycleCode, statementType = 'balance_sheet') => {
    setLineItemData((prevData) => {
      const currentItem = getLineItemBySoxItemId(prevData, itemId);
      const currentCycles = Array.isArray(currentItem?.sox_cycles) && currentItem.sox_cycles.length > 0
        ? currentItem.sox_cycles
        : currentItem?.sox_cycle
          ? [currentItem.sox_cycle]
        : getSoxCycles(currentItem?.label || '', currentItem, statementType);
      let newCycles;

      if (currentCycles.includes(cycleCode)) {
        newCycles = currentCycles.filter((c) => c !== cycleCode);
      } else {
        newCycles = [...currentCycles, cycleCode];
      }

      if (newCycles.length === 0) {
        newCycles = ['ELC'];
      }

      setAllLineItemData((prevBundles) =>
        Array.isArray(prevBundles)
          ? prevBundles.map((bundle) =>
              bundle.documentIndex === activeLineItemIndex
                ? {
                    ...bundle,
                    lineItems: updateLineItemSoxCyclesByItemId(bundle.lineItems, itemId, newCycles)
                  }
                : bundle
            )
          : prevBundles
      );
      return updateLineItemSoxCyclesByItemId(prevData, itemId, newCycles);
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    const enrichLineItems = (lineItems) => {
      if (!lineItems) return lineItems;
      let changed = false;
      const bsItems = lineItems?.balance_sheet?.items;
      const nextBsItems = Array.isArray(bsItems)
        ? bsItems.map((item) => {
            if (
              Array.isArray(item.sox_cycles) && item.sox_cycles.length > 0 &&
              item?.sox_cycle != null && item.sox_cycle !== ''
            ) return item;
            changed = true;
            let resolvedSoxCycles;
            if (Array.isArray(item.sox_cycles) && item.sox_cycles.length > 0) {
              resolvedSoxCycles = item.sox_cycles;
            } else if (item?.sox_cycle) {
              resolvedSoxCycles = [item.sox_cycle];
            } else {
              resolvedSoxCycles = getSoxCycles(item.label, item, 'balance_sheet');
            }
            return {
              ...item,
              sox_cycles: resolvedSoxCycles,
              sox_cycle: item?.sox_cycle ?? resolvedSoxCycles?.[0] ?? null
            };
          })
        : bsItems;
      const isItems = lineItems?.income_statement?.items;
      const nextIsItems = Array.isArray(isItems)
        ? isItems.map((item) => {
            if (
              Array.isArray(item.sox_cycles) && item.sox_cycles.length > 0 &&
              item?.sox_cycle != null && item.sox_cycle !== ''
            ) return item;
            changed = true;
            let resolvedSoxCycles;
            if (Array.isArray(item.sox_cycles) && item.sox_cycles.length > 0) {
              resolvedSoxCycles = item.sox_cycles;
            } else if (item?.sox_cycle) {
              resolvedSoxCycles = [item.sox_cycle];
            } else {
              resolvedSoxCycles = getSoxCycles(item.label, item, 'income_statement');
            }
            return {
              ...item,
              sox_cycles: resolvedSoxCycles,
              sox_cycle: item?.sox_cycle ?? resolvedSoxCycles?.[0] ?? null
            };
          })
        : isItems;
      if (!changed) return lineItems;
      return {
        ...lineItems,
        ...(Array.isArray(bsItems)
          ? {
              balance_sheet: {
                ...lineItems.balance_sheet,
                items: nextBsItems
              }
            }
          : {}),
        ...(Array.isArray(isItems)
          ? {
              income_statement: {
                ...lineItems.income_statement,
                items: nextIsItems
              }
            }
          : {})
      };
    };

    setLineItemData((prev) => enrichLineItems(prev));
    setAllLineItemData((prev) =>
      Array.isArray(prev)
        ? prev.map((bundle) => ({
            ...bundle,
            lineItems: enrichLineItems(bundle.lineItems)
          }))
        : prev
    );
  }, [soxDefaultsMappings, getSoxCycles]);

  // Render a line item with editable SOX cycles
  // balanceSheetItemsRowIndex: index in lineItemData.balance_sheet.items (must match Risk Assessment note keys)
  const renderLineItemWithSoxEdit = (
    item,
    idx,
    itemType,
    statementType = 'balance_sheet',
    annualizationFactor = 1,
    balanceSheetItemsRowIndex = null
  ) => {
    const itemId = `${itemType}_${idx}_${item.label.replace(/\s+/g, '_').toLowerCase()}`;
    const riskRowIndex =
      statementType === 'income_statement'
        ? idx
        : balanceSheetItemsRowIndex != null
          ? balanceSheetItemsRowIndex
          : idx;
    const risk = getRiskScore(item.label, statementType, riskRowIndex);
    const soxCycles = getSoxCycles(item.label, item, statementType);
    const isEditing = editingItem === itemId;
    const paddingClass = statementType === 'income_statement' ? 'px-6' : 'px-10';

    const noteRowIndex =
      statementType === 'income_statement'
        ? idx
        : balanceSheetItemsRowIndex != null
          ? balanceSheetItemsRowIndex
          : idx;
    const legacyNoteKey = `${statementType === 'income_statement' ? 'is' : 'bs'}_${activeLineItemIndex}_${noteRowIndex}`;
    const typedIncomeNoteKey =
      statementType === 'income_statement'
        ? `${item.category === 'revenue' ? 'is_revenue' : 'is_expense'}_${activeLineItemIndex}_${idx}`
        : null;
    const legacyIncomeNoteKey =
      statementType === 'income_statement' ? `income_statement_${activeLineItemIndex}_${idx}` : null;
    const clientNoteKey = item[CLIENT_LINE_ITEM_UUID_KEY];
    const noteLookupKeys = [clientNoteKey, legacyNoteKey, typedIncomeNoteKey, legacyIncomeNoteKey].filter(
      (k) => k != null && k !== ''
    );
    const matchedKey = noteLookupKeys.find((nk) => Object.prototype.hasOwnProperty.call(lineItemNotes, nk));
    const raw = matchedKey != null ? lineItemNotes[matchedKey] : undefined;
    const resolvedNote = raw == null ? '' : String(raw);

    const parsedAnnualizationFactor = Number(annualizationFactor);
    const normalizedAnnualizationFactor =
      Number.isFinite(parsedAnnualizationFactor) && parsedAnnualizationFactor > 0
        ? parsedAnnualizationFactor
        : 1;
    const displayValue = (Number(item.value) || 0) * normalizedAnnualizationFactor;

    // Show first 3 cycles, rest in expandable section
    const visibleCycles = soxCycles.slice(0, 3);
    const hiddenCyclesCount = Math.max(0, soxCycles.length - 3);

    return (
      <div key={idx} className="relative">
        <div className={`grid grid-cols-[3fr_2fr_1.5fr_3fr_2fr] gap-4 items-center ${paddingClass} py-3 hover:bg-gray-50 border-b border-gray-100`}>
          {/* Line Item Name */}
          <div>
            <span className="text-sm text-gray-700">{item.label}</span>
          </div>

          {/* Amount */}
          <div className="text-right">
            <span className="text-sm text-gray-900 font-medium">{formatLineItemValue(displayValue)}</span>
          </div>

          {/* Risk Category */}
          <div className="flex justify-center">
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getRiskBadgeColor(risk?.risk_category)}`}>
              {risk?.risk_category ? risk.risk_category.toUpperCase() : 'MODERATE'}
              {risk?.risk_score !== undefined && ` (${risk.risk_score.toFixed(1)})`}
            </span>
          </div>

          {/* Notes Column */}
          <div>
            {editingNoteKey === (clientNoteKey || legacyNoteKey) ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={resolvedNote}
                  onChange={(e) =>
                    setLineItemNotes((prev) => ({
                      ...prev,
                      [clientNoteKey || legacyNoteKey]: e.target.value,
                    }))
                  }
                  placeholder="Add notes..."
                  className="flex-1 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <button
                  onClick={() => setEditingNoteKey(null)}
                  className="p-1 text-green-600 hover:text-green-700"
                >
                  <CheckCircle className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="flex-1 text-sm text-gray-700">
                  {resolvedNote || <span className="text-gray-400 italic">No notes</span>}
                </span>
                <button
                  onClick={() => setEditingNoteKey(clientNoteKey || legacyNoteKey)}
                  className="p-1 text-gray-400 hover:text-blue-600"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* SOX Cycles */}
          <div className="flex gap-1 items-center justify-end">
            {visibleCycles.map((cycle, cycleIdx) => {
              const code = typeof cycle === 'string' ? cycle.trim() : String(cycle ?? '');
              const meta = [...availableSoxCycles, ...customSoxCycles].find((c) => c.code === code);
              const tip = meta?.name || getSoxCycleNameFromReference(code);
              return (
                <span key={cycleIdx} className="relative group">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium cursor-help">
                    {code}
                  </span>
                  {tip && (
                    <span className="pointer-events-none invisible group-hover:visible absolute right-0 top-full mt-1 z-50 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap shadow-lg">
                      {tip}
                    </span>
                  )}
                </span>
              );
            })}
            {hiddenCyclesCount > 0 && (
              <button
                onClick={() => setEditingItem(isEditing ? null : itemId)}
                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 transition-colors"
              >
                +{hiddenCyclesCount}
              </button>
            )}
            {hiddenCyclesCount === 0 && (
              <button
                onClick={() => setEditingItem(isEditing ? null : itemId)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isEditing ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
        </div>
        
        {/* SOX Cycle Dropdown */}
        {isEditing && (
          <div className="absolute right-10 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-5 min-w-[700px]">
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Select SOX Cycles:</h4>
              <div className="grid grid-cols-5 gap-3">
                {[...availableSoxCycles, ...customSoxCycles].map(cycle => (
                  <label
                    key={cycle.code}
                    className={`flex flex-col items-center gap-1 cursor-pointer border rounded-lg p-2 transition-all hover:bg-gray-50 ${
                      soxCycles.includes(cycle.code) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                    title={`${cycle.name ?? ''}${cycle.description ? ' - ' + cycle.description : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={soxCycles.includes(cycle.code)}
                      onChange={() => handleSoxCycleToggle(itemId, cycle.code, statementType)}
                      className="sr-only"
                    />
                    <span className={`text-sm font-bold ${
                      soxCycles.includes(cycle.code) ? 'text-blue-700' : 'text-gray-700'
                    }`}>
                      {cycle.code}
                    </span>
                    <span className="text-xs text-gray-600 text-center leading-tight">
                      {(cycle.name || '').split(' ').slice(0, 2).join(' ')}
                    </span>
                  </label>
                ))}

                {/* Add Custom SOX Cycle Button */}
                {!showAddSoxCycleForm ? (
                  <button
                    onClick={() => setShowAddSoxCycleForm(true)}
                    className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 rounded-lg p-2 transition-all hover:border-blue-500 hover:bg-blue-50"
                  >
                    <span className="text-2xl text-gray-400">+</span>
                    <span className="text-xs text-gray-600 text-center">Add Custom</span>
                  </button>
                ) : (
                  <div className="col-span-2 border-2 border-blue-500 rounded-lg p-3 bg-blue-50">
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        placeholder="Code (e.g., ABC)"
                        value={newSoxCycle.code}
                        onChange={(e) =>
                          setNewSoxCycle((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                        }
                        maxLength={3}
                        className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="name"
                        value={newSoxCycle.description}
                        onChange={(e) =>
                          setNewSoxCycle((prev) => ({ ...prev, description: e.target.value }))
                        }
                        className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            const err = getCustomSoxCycleValidationError(
                              newSoxCycle.code,
                              newSoxCycle.description,
                              availableSoxCycles,
                              customSoxCycles
                            );
                            if (err) {
                              toast.error(String(err));
                              return;
                            }
                            const code = (newSoxCycle.code || '').trim().toUpperCase();
                            const description = (newSoxCycle.description || '').trim();
                            setCustomSoxCycles((prev) => [
                              ...prev,
                              { code, name: description, description }
                            ]);
                            setNewSoxCycle({ code: '', description: '' });
                            setShowAddSoxCycleForm(false);
                          }}
                          className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => {
                            setNewSoxCycle({ code: '', description: '' });
                            setShowAddSoxCycleForm(false);
                          }}
                          className="flex-1 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t">
              <button
                onClick={() => {
                  const defaultCycles = resolveSoxCyclesWithDefaults({
                    mappings: soxDefaultsMappings,
                    itemLabel: item.label,
                  });
                  setLineItemData((prevData) => updateLineItemSoxCyclesByItemId(prevData, itemId, defaultCycles));
                  setAllLineItemData((prevBundles) =>
                    Array.isArray(prevBundles)
                      ? prevBundles.map((bundle) =>
                          bundle.documentIndex === activeLineItemIndex
                            ? {
                                ...bundle,
                                lineItems: updateLineItemSoxCyclesByItemId(bundle.lineItems, itemId, defaultCycles)
                              }
                            : bundle
                        )
                      : prevBundles
                  );
                }}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Clear assignments
              </button>
              <button
                onClick={() => setEditingItem(null)}
                className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  /** Full replace on upload step (browse): clear server entries + analysis cache + downstream. */
  const resetAfterFullFileReplace = useCallback(() => {
    setUploadedFileEntries([]);
    setDocumentAnalysisResults([]);
    resetDownstreamAfterUploadListChange();
  }, [resetDownstreamAfterUploadListChange]);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Max files check to check the no of files cannot be more than MAX_FILES
    if (files.length > MAX_FILES) {
      toast.error(`You can upload a maximum of ${MAX_FILES} files.`);
      return;
    }

    // Validate file types - allow PDF, XLSX, CSV
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];

    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error('Please upload only PDF, XLSX, or CSV files');
      return;
    }

    // Enforce max file size per file
    const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      toast.error(`File cannot be more than 50 MB.`);
      return;
    }

    setUploadedFiles(files);
    resetAfterFullFileReplace();
    setUploadProgress(100);
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) {
        setUploadProgress(0);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
      return next;
    });
    setUploadedFileEntries((prev) =>
      Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
    );
    setDocumentAnalysisResults((prev) =>
      Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []
    );
    setSelectedDocumentIndices((prev) => remapIndicesAfterRemoval(prev, index));
    setRiskAssessmentDocIndices((prev) => remapIndicesAfterRemoval(prev, index));
    resetDownstreamAfterUploadListChange();
  };

  const handleChangeFile = () => {
    fileInputRef.current?.click();
  };

  const handleRestartFlow = () => {
    // Confirm before restarting
    const confirmed = window.confirm(
      'Are you sure you want to restart the entire flow? All unsaved progress will be lost.'
    );

    if (!confirmed) return;

    // Reset all state variables to initial values
    setCurrentStep(1);
    setUploadedFiles([]);
    setUploadedFileEntries([]);
    setDocumentAnalysisResults([]);
    setSelectedDocumentIndices([]);
    setRiskAssessmentDocIndices([]);
    setSelectedAnalysisYear('');
    setLastAnalyzedFiscalYear('');
    setShowDifferentYearWarning(false);
    setDetectedMismatchYear('');
    setIsProcessing(false);
    setUploadProgress(0);
    setCompanyProfile(null);
    setDocumentInfo(null);
    setSelectedCategory('Net Income');
    setMaterialityRecommendation(null);
    setRecommendationReasoning('');
    setRecommendationConfidence('medium');
    setSensitivityLevel('Standard');
    setOverrideAmount('');
    setOverrideReasoning('');
    setAnalysisProgress(0);
    setAnalysisSteps([
      { name: 'Analyzing Industry Vertical', status: 'pending' },
      { name: 'Processing Financial Metrics', status: 'pending' },
      { name: 'Assessing Risk Profile', status: 'pending' },
      { name: 'Determining Materiality Category', status: 'pending' }
    ]);
    setExpandedSections({
      balanceSheet: true,
      assets: true,
      currentAssets: true,
      nonCurrentAssets: true,
      liabilities: true,
      currentLiabilities: true,
      nonCurrentLiabilities: true,
      equity: true,
      incomeStatement: true
    });
    setEditingItem(null);
    setActiveDocumentIndex(null);
    setIsUpdatingProfile(false);
    setAllDocumentResponses([]);
    setLineItemData(null);
    setIsLoadingLineItems(false);
    setConfirmedMateriality(null);
    setAllLineItemData([]);
    setActiveLineItemIndex(0);
    setRiskScores(null);
    setCustomRiskLevels({});
    setIsCalculatingRisk(false);
    setShowVersionModal(false);
    setVersionName('');
    setIsSavingAssessment(false);
    setEntityScopingDataForSave(null);
    setRiskAnalysisProgress('');
    setEditingRiskScore(null);
    setTrialBalanceFile(null);
    setIsUploadingTrialBalance(false);
    setShowScopingMatrix(false);
    setScopingFilter('all');
    setThresholdOverride('');
    setThresholdJustification('');
    setExpandedGLCategories({});
    setShowThresholdOverride(false);
    setGlEntriesData(null);
    setIsAnalyzingTrialBalance(false);
    setShowScopingReport(false);
    setIsLoadingEntities(false);
    setEntityScopingData([]);
    setEntityPeriod('');
    setEntitiesAnalyzeError(null);
    setEntityAnalyzeDialog({ open: false, title: '', subtitle: '', message: '', variant: 'warning' });
    setShowAddLineItemForm({ show: false, statementType: null, category: null });
    setNewLineItem({
      label: '',
      value: '',
      riskScore: '',
      note: '',
      balanceSheetCategory: 'asset',
      incomeStatementCategory: 'revenue'
    });
    setCustomSoxCycles([]);
    setShowAddSoxCycleForm(false);
    setNewSoxCycle({ code: '', description: '' });

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (trialBalanceInputRef.current) {
      trialBalanceInputRef.current.value = '';
    }

    // Clear localStorage
    localStorage.removeItem(MATERIALITY_ASSESSMENT_STORAGE_KEY);

    console.log('Flow restarted - all state reset to initial values');
  };

  // Presigned URL upload: use common utils with MATERIALITY_ASSESSMENT module
  const handleProceedToDocumentAnalysis = useCallback(async () => {
    if (uploadedFiles.length === 0) return;
    if (analyzeDocumentsInFlightRef.current) return;
    if (!selectedAnalysisYear) {
      toast.error('Please select a fiscal year before analyzing documents.');
      return;
    }

    const resolvedUserHash = localStorage.getItem('userHash') || user?.user_hash || '';
    if (resolvedUserHash) {
      localStorage.setItem('userHash', resolvedUserHash);
    }
    const userHash = localStorage.getItem('userHash') || user?.user_hash || '';

    const prevDocsSnapshot = documentAnalysisResults;
    const shouldReanalyzeForYear = lastAnalyzedFiscalYear !== selectedAnalysisYear;
    const entriesResolved = resolveUploadedFileEntriesForAnalyze(uploadedFiles, uploadedFileEntries);
    const { actions, isValid } = getAnalyzeDocumentsSlotActions(uploadedFiles, entriesResolved);

    if (!isValid) {
      toast.error(
        'One or more documents have no file on the server. Re-select those files on step 2 (or remove the extra row), then click Analyze Documents.'
      );
      return;
    }

    const n = uploadedFiles.length;
    const uploadActions = actions.filter((a) => a.type === 'upload');
    const actionsByIndex = analyzeSlotActionsByIndex(actions);

    const clampDocIndices = (indices) =>
      [...new Set((indices || []).filter((i) => Number.isInteger(i) && i >= 0 && i < n))].sort((a, b) => a - b);

    setCurrentStep(3);
    setIsProcessing(true);
    setAnalysisProgress(0);
    setShowDifferentYearWarning(false);
    setDetectedMismatchYear('');
    analyzeDocumentsInFlightRef.current = true;

    try {
      const mergedEntries = actions
        .filter((a) => a.type === 'reuse')
        .reduce((acc, a) => {
          acc[a.index] = mergedEntryFromReuseSlot(a.entry);
          return acc;
        }, new Array(n));

      if (uploadActions.length > 0) {
        const sortedUploads = [...uploadActions].sort((x, y) => x.index - y.index);
        const entriesWithRef = await uploadFilesAndComplete(
          sortedUploads.map((a) => a.file),
          userHash,
          MODULE_UPLOAD.MATERIALITY_ASSESSMENT
        );
        sortedUploads.forEach((a, j) => {
          const { file_ref, ...item } = entriesWithRef[j];
          mergedEntries[a.index] = item;
        });
      }

      const badSlot = Array.from({ length: n }, (_, i) => i).find((i) => {
        const a = actionsByIndex[i];
        if (!a || a.type === 'missing') return false;
        return !entryHasServerFileId(mergedEntries[i]);
      });
      if (badSlot !== undefined) {
        throw new Error(`Analyze documents: missing file_id for slot ${badSlot + 1}.`);
      }

      setUploadedFileEntries(mergedEntries);

      const analysisPairs = Array.from({ length: n }, (_, i) => i).flatMap((i) => {
        const a = actionsByIndex[i];
        if (!a) return [];
        if (a.type === 'upload') return [[i, mergedEntries[i]]];
        if (
          a.type === 'reuse' &&
          (shouldReanalyzeForYear || !hasDocumentAnalysisCache(prevDocsSnapshot[i]))
        ) {
          return [[i, mergedEntries[i]]];
        }
        return [];
      });
      const needAnalysisIndices = analysisPairs.map(([i]) => i);
      const analyzePayloadEntries = analysisPairs.map(([, entry]) => entry);

      const nextDocs = Array.from({ length: n }, (_, i) => prevDocsSnapshot[i] ?? null);

      const needsAnalyzeDocumentsApi = analyzePayloadEntries.length > 0;

      if (needsAnalyzeDocumentsApi) {
        setLineItemData(null);
        setAllLineItemData([]);
        setRiskScores(null);
        setAllDocumentResponses([]);
        setCompanyProfile(null);
        setDocumentInfo(null);
        setConfirmedMateriality(null);

        const filesPayload = entriesForAnalyzeDocumentsRequest(analyzePayloadEntries);
        const response = await axiosInstance.post(
          '/api/materiality/analyze-documents',
          {
            files: filesPayload,
            fiscalYear: selectedAnalysisYear,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: userHash
            },
            timeout: 120000
          }
        );

        const returned = response.data.documents || [];
        const apiDifferentYearFlag = response.data?.is_different_year === true;
        const documentLevelYearMismatch = returned.some(
          (doc) => doc?.year_mismatch === true
        );
        const mismatchDocFromApi = returned.find(
          (doc) => doc?.year_mismatch === true
        );
        const detectedYearFromApi = mismatchDocFromApi?.detected_year
          ? String(mismatchDocFromApi.detected_year)
          : '';
        if (returned.length !== needAnalysisIndices.length) {
          throw new Error(
            `Analyze documents: expected ${needAnalysisIndices.length} results, received ${returned.length}`
          );
        }
        needAnalysisIndices.forEach((idx, j) => {
          nextDocs[idx] = returned[j];
        });
        setShowDifferentYearWarning(
          apiDifferentYearFlag || documentLevelYearMismatch
        );
        setDetectedMismatchYear(detectedYearFromApi);
      } else {
        const mismatchDocFromCache = nextDocs.find(
          (doc) => doc?.year_mismatch === true
        );
        const detectedYearFromCache = mismatchDocFromCache?.detected_year
          ? String(mismatchDocFromCache.detected_year)
          : '';
        setShowDifferentYearWarning(
          nextDocs.some((doc) => doc?.year_mismatch === true)
        );
        setDetectedMismatchYear(detectedYearFromCache);
      }

      setDocumentAnalysisResults(nextDocs);
      setLastAnalyzedFiscalYear(selectedAnalysisYear);

      setSelectedDocumentIndices((prev) => clampDocIndices(prev));
      setRiskAssessmentDocIndices((prev) => clampDocIndices(prev));

      setIsProcessing(false);
    } catch (error) {
      console.error('Error analyzing documents:', error);
      setIsProcessing(false);
      setCurrentStep(2);
      setDetectedMismatchYear('');
      toast.error(error?.message || 'Error analyzing documents. Please try again.');
    } finally {
      analyzeDocumentsInFlightRef.current = false;
    }
  }, [uploadedFiles, uploadedFileEntries, documentAnalysisResults, user, selectedAnalysisYear, lastAnalyzedFiscalYear]);

  // Helper function to aggregate data from multiple documents
  const aggregateMultipleDocuments = (responses) => {
    const documents = [];
    const fieldSources = {
      totalRevenue: [],
      netIncome: [],
      totalAssets: []
    };

    // Collect all data from responses
    responses.forEach((response) => {
      const extractedData = response.data || response.extractedData || {};

      documents.push({
        companyName: extractedData.companyName || response.company_name || 'Unknown Company',
        documentType: extractedData.documentType || response.category || response.documentCategory || 'Unknown',
        period: extractedData.period || response.period || '',
        periodMonths: extractedData.periodMonths || 12,
        annualizationFactor: extractedData.annualizationFactor || 1,
        sourceFile: response.sourceFile || response.filename,
        file_id: response.file_id || null,
        is_forecast: response.is_forecast || false,
        forecast_type: response.forecast_type || null
      });

      // Track which documents provide which fields
      if (extractedData.totalRevenue && extractedData.totalRevenue !== 0) {
        fieldSources.totalRevenue.push({
          value: extractedData.totalRevenue,
          source: response.sourceFile,
          documentType: extractedData.documentType || response.documentCategory,
          annualizationFactor: extractedData.annualizationFactor || 1,
          rawValue: extractedData.annualizationFactor > 1 ? extractedData.originalValues.total_revenue : null,
          is_forecast: response.is_forecast || false,
          forecast_type: response.forecast_type || null
        });
      }

      if (extractedData.netIncome && extractedData.netIncome !== 0) {
        fieldSources.netIncome.push({
          value: extractedData.netIncome,
          source: response.sourceFile,
          documentType: extractedData.documentType || response.documentCategory,
          annualizationFactor: extractedData.annualizationFactor || 1,
          rawValue: extractedData.annualizationFactor > 1 ? extractedData.originalValues.net_income : null,
          is_forecast: response.is_forecast || false,
          forecast_type: response.forecast_type || null
        });
      }

      if (extractedData.totalAssets && extractedData.totalAssets !== 0) {
        fieldSources.totalAssets.push({
          value: extractedData.totalAssets,
          source: response.sourceFile,
          documentType: extractedData.documentType || response.documentCategory,
          annualizationFactor: 1, // Total Assets is a balance sheet item, not annualized
          rawValue: null,
          is_forecast: response.is_forecast || false,
          forecast_type: response.forecast_type || null
        });
      }
    });

    // Use first non-zero value for company profile (prioritize 10-K/10-Q over Forecast)
    const getPrimaryValue = (fieldName) => {
      const sources = fieldSources[fieldName];
      if (sources.length === 0) return 0;

      // Prioritize 10-K/10-Q
      const tenKQ = sources.find(s => s.documentType === '10-K' || s.documentType === '10-Q');
      return tenKQ ? tenKQ.value : sources[0].value;
    };

    // Get primary document for company info
    const primaryResponse = responses.find(r => r.documentCategory === '10-K' || r.documentCategory === '10-Q') || responses[0];
    const primaryData = primaryResponse.data || primaryResponse.extractedData || {};

    return {
      documentInfo: {
        documents: documents, // Array of all document info
        fieldSources: fieldSources // Track which documents provide which fields
      },
      companyProfile: {
        companyName: primaryData.companyName || 'Unknown Company',
        industry: primaryData.industry || 'Technology Services',
        totalRevenue: getPrimaryValue('totalRevenue'),
        netIncome: getPrimaryValue('netIncome'),
        totalAssets: getPrimaryValue('totalAssets'),
        growthStage: primaryData.growthStage || 'Mature',
        tradingSymbol: primaryData.tradingSymbol || ''
      }
    };
  };

  // Function to switch between documents
  const handleDocumentSwitch = (documentIndex) => {
    if (documentIndex === activeDocumentIndex) return; // Already selected

    // Trigger animation
    setIsUpdatingProfile(true);

    // Switch after a brief animation
    setTimeout(() => {
      setActiveDocumentIndex(documentIndex);

      // Update company profile with selected document's data
      const selectedResponse = allDocumentResponses[documentIndex];
      const extractedData = selectedResponse.data || selectedResponse.extractedData || {};

      // Get the 10-Q response for fallback
      const tenKQResponse = allDocumentResponses.find(r => r.documentCategory === '10-K' || r.documentCategory === '10-Q');
      const fallbackData = tenKQResponse ? (tenKQResponse.data || tenKQResponse.extractedData || {}) : {};

      // Helper to format source with forecast info
      const formatSource = (response) => {
        if (!response) return 'Unknown';
        let source = response.documentCategory;
        if (response.is_forecast) {
          source += ' (Forecast';
          if (response.forecast_type) {
            source += ` - ${response.forecast_type}`;
          }
          source += ')';
        }
        return source;
      };

      // Track source for each field
      const revenueSource = extractedData.totalRevenue ? formatSource(selectedResponse) : formatSource(tenKQResponse);
      const netIncomeSource = extractedData.netIncome ? formatSource(selectedResponse) : formatSource(tenKQResponse);
      const assetsSource = extractedData.totalAssets ? formatSource(selectedResponse) : formatSource(tenKQResponse);

      // Update company profile with fallback logic and source tracking
      setCompanyProfile({
        companyName: extractedData.companyName || fallbackData.companyName || 'Unknown Company',
        industry: extractedData.industry || fallbackData.industry || 'Technology Services',
        totalRevenue: extractedData.totalRevenue || fallbackData.totalRevenue || 0,
        netIncome: extractedData.netIncome || fallbackData.netIncome || 0,
        totalAssets: extractedData.totalAssets || fallbackData.totalAssets || 0,
        growthStage: extractedData.growthStage || fallbackData.growthStage || 'Mature',
        tradingSymbol: extractedData.tradingSymbol || fallbackData.tradingSymbol || '',
        // Add source tracking
        revenueSource,
        netIncomeSource,
        assetsSource
      });

      // End animation
      setTimeout(() => {
        setIsUpdatingProfile(false);
      }, 300);
    }, 150);
  };

  const handleProceedToMaterialityAnalysis = async () => {
    if (
      uploadedFiles.length === 0 ||
      selectedDocumentIndices.length === 0 ||
      !riskAssessmentDocIndices ||
      riskAssessmentDocIndices.length === 0 ||
      !selectedAnalysisYear
    ) {
      toast.error('Please select at least one document for both Materiality and Risk Assessment and choose a fiscal year before continuing.');
      return;
    }

    setRiskScores(null);
    setConfirmedMateriality(null);

    // Re-running analyze-pnl: clear step-3 overrides so displayed materiality matches the new API result.
    setCustomOverallMateriality('');
    setCustomPlanningMateriality('');
    setCustomDeminimis('');
    setOverrideAmount('');
    setOverrideReasoning('');
    setShowReasoningBox(false);
    setCustomCategories([]);
    setSensitivityLevel('Standard');

    setCurrentStep(4);
    setIsProcessing(true);
    setAnalysisProgress(0);
    setAnalysisError(null); // clear any previous error on retry

    // Reset analysis steps
    setAnalysisSteps([
      { name: 'Analyzing Industry Vertical', status: 'processing' },
      { name: 'Processing Financial Metrics', status: 'pending' },
      { name: 'Assessing Risk Profile', status: 'pending' },
      { name: 'Determining Materiality Category', status: 'pending' }
    ]);

    try {
      const userHash = localStorage.getItem('userHash');

      // Simulate progress updates
      let progressInterval = setInterval(() => {
        setAnalysisProgress(prev => {
          if (prev >= 75) {
            clearInterval(progressInterval);
            return 75;
          }
          return prev + 5;
        });
      }, 200);

      // Update steps progressively
      const stepTimings = [0, 1000, 2000, 3000];
      stepTimings.forEach((timing, index) => {
        setTimeout(() => {
          setAnalysisSteps(steps => steps.map((step, idx) => ({
            ...step,
            status: idx < index ? 'completed' : idx === index ? 'processing' : 'pending'
          })));
        }, timing);
      });

      // Process each selected document - reuse file_id if already uploaded (from init+complete in upload step)
      const allResponses = await selectedDocumentIndices.reduce(
        async (prevPromise, index) => {
          const acc = await prevPromise;
          const selectedFile = uploadedFiles[index];
          const selectedDoc = documentAnalysisResults[index];
          const existingEntry = uploadedFileEntries[index];
          const fileLabel = getUploadSlotLabel(selectedFile, index, selectedDoc?.filename);

          console.log(`Processing document ${index}: ${fileLabel}, Category: ${selectedDoc.category}`);

          let file_id;
          if (existingEntry?.file_id || existingEntry?.uuid) {
            file_id = existingEntry.file_id || existingEntry.uuid;
          } else {
            if (!isRealUploadFile(selectedFile)) {
              throw new Error(
                `Missing stored file id for ${fileLabel}. Return to step 2, re-select the file, and run Analyze Documents again.`
              );
            }
            const initResult = await initUpload(selectedFile, userHash, MODULE_UPLOAD.MATERIALITY_ASSESSMENT);
            const { upload_url, content_type } = initResult;
            const file_ref = initResult.file_ref;

            await uploadToGCS(upload_url, selectedFile, content_type);
            const completeResult = await completeUpload(
              file_ref,
              selectedFile.name,
              content_type || selectedFile.type || 'application/octet-stream',
              selectedFile.size,
              userHash,
              MODULE_UPLOAD.MATERIALITY_ASSESSMENT
            );
            file_id = completeResult.uuid;
          }

          if (!file_id) {
            throw new Error(`Missing file id for document: ${fileLabel}. Re-upload the file or restart from upload step.`);
          }

          const response = await axiosInstance.post(
            '/api/materiality/analyze-pnl',
            {
              file_id,
              sessionId: `session_${Date.now()}_${index}`,
              userHash: userHash,
              documentCategory: selectedDoc.category || '10-K',
              fiscalYear: selectedAnalysisYear,
            },
            {
              timeout: 120000,
            }
          );

          return [
            ...acc,
            {
              ...response.data,
              sourceFile: fileLabel,
              documentCategory: selectedDoc.category,
              file_id,
            },
          ];
        },
        Promise.resolve([])
      );

      clearInterval(progressInterval);
      setAnalysisProgress(100);

      // Store all responses for document switching
      setAllDocumentResponses(allResponses);

      // Aggregate results from all documents
      const aggregatedData = aggregateMultipleDocuments(allResponses);

      // Set document information (now with multiple documents)
      setDocumentInfo(aggregatedData.documentInfo);

      // Set the first document as active by default
      setActiveDocumentIndex(0);

      // Extract materiality recommendation (use the first 10-K/10-Q if available, otherwise first forecast)
      const primaryDoc = allResponses.find(r => r.documentCategory === '10-K' || r.documentCategory === '10-Q') || allResponses[0];
      const extractedData = primaryDoc.data || primaryDoc.extractedData || {};

      const recommendation = primaryDoc.materialityRecommendation || extractedData.materialityRecommendation || 'Total Revenue';
      const reasoning = primaryDoc.recommendationReasoning || extractedData.recommendationReasoning || '';
      const confidence = primaryDoc.recommendationConfidence || extractedData.recommendationConfidence || 'medium';

      setMaterialityRecommendation(recommendation);
      setRecommendationReasoning(reasoning);
      setRecommendationConfidence(confidence);
      setSelectedCategory(recommendation);

      // Set company profile with aggregated data and add source tracking
      const getSourceWithForecast = (fieldSource) => {
        if (!fieldSource) return 'Unknown';
        let source = fieldSource.documentType;
        if (fieldSource.is_forecast) {
          source += ' (Forecast';
          if (fieldSource.forecast_type) {
            source += ` - ${fieldSource.forecast_type}`;
          }
          source += ')';
        }
        return source;
      };

      const profileWithSources = {
        ...aggregatedData.companyProfile,
        // Add sources based on fieldSources
        revenueSource: getSourceWithForecast(aggregatedData.documentInfo.fieldSources.totalRevenue?.[0]),
        netIncomeSource: getSourceWithForecast(aggregatedData.documentInfo.fieldSources.netIncome?.[0]),
        assetsSource: getSourceWithForecast(aggregatedData.documentInfo.fieldSources.totalAssets?.[0])
      };
      setCompanyProfile(profileWithSources);




      // Mark all steps as completed
      setAnalysisSteps(steps => steps.map(step => ({ ...step, status: 'completed' })));

      setTimeout(() => {
        setIsProcessing(false);
      }, 500);

    } catch (error) {
      console.error('Error processing documents:', error);
      setIsProcessing(false);
      // Mark the stuck step as failed so the UI shows a clear failure state
      setAnalysisSteps(steps => steps.map(step =>
        step.status === 'processing' ? { ...step, status: 'failed' } : step
      ));
      const errorCode = error?.response?.data?.details?.ai_error_code || '';
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Error processing the documents. Please try again.';
      setAnalysisError({ message, code: errorCode });
    }
  };

  const calculateMateriality = () => {
    if (!companyProfile) return 0;

    if (overrideAmount) {
      return parseFloat(overrideAmount.replace(/,/g, ''));
    }

    // Check if selected category is a custom category
    const customCategory = customCategories.find(cat => cat.name === selectedCategory);

    let baseAmount;
    if (customCategory) {
      // Use custom category amount
      baseAmount = customCategory.amount;
    } else {
      // Use standard category amounts
      baseAmount = selectedCategory === 'Net Income'
        ? companyProfile.netIncome
        : selectedCategory === 'Total Revenue'
        ? companyProfile.totalRevenue
        : selectedCategory === 'Total Assets'
        ? companyProfile.totalAssets
        : companyProfile.netIncome;
    }

    // Get the actual percentage based on category and sensitivity level
    const getPercentage = () => {
      // For custom categories, use the custom percentage
      if (customCategory) {
        const basePercentage = customCategory.percentage / 100; // Convert to decimal
        if (sensitivityLevel === 'Conservative') return basePercentage / 2;
        if (sensitivityLevel === 'Moderate') return basePercentage * 2;
        return basePercentage; // Standard
      }

      // For standard categories
      if (selectedCategory === 'Net Income') {
        if (sensitivityLevel === 'Conservative') return 0.025;
        if (sensitivityLevel === 'Moderate') return 0.075;
        return 0.05; // Standard
      } else if (selectedCategory === 'Total Revenue') {
        if (sensitivityLevel === 'Conservative') return 0.005;
        if (sensitivityLevel === 'Moderate') return 0.02;
        return 0.01; // Standard
      } else if (selectedCategory === 'Total Assets') {
        if (sensitivityLevel === 'Conservative') return 0.005;
        if (sensitivityLevel === 'Moderate') return 0.02;
        return 0.01; // Standard
      }
      return 0.05; // Default
    };

    return baseAmount * getPercentage();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatLineItemValue = (value) => {
    if (value === null || value === undefined) return '$0';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(absValue);
    return isNegative ? `-$${formatted}` : `$${formatted}`;
  };

  const getRiskScore = (itemLabel, statementType = 'balance_sheet', itemIndex = null) => {
    if (!riskScores) return null;

    const isMultiDoc = allLineItemData.length > 1;
    const currentDocRiskScores = getRiskScoresForActiveDocument(
      riskScores,
      activeLineItemIndex,
      isMultiDoc
    );
    if (!currentDocRiskScores) return null;

    const statementData = currentDocRiskScores[statementType];
    if (!statementData) {
      return null;
    }

    const normalizeLabel = (label) => String(label ?? '').toLowerCase().trim();

    const searchLabel = normalizeLabel(itemLabel);

    // First, check for v2 structure (flat items array)
    // Prefer client UUID (stamped on risk rows after assessment), then index, then label.
    if (statementData.items && Array.isArray(statementData.items)) {
      let item = null;
      const rowFromUi = lineItemData?.[statementType]?.items?.[itemIndex];
      const clientUid = rowFromUi?.[CLIENT_LINE_ITEM_UUID_KEY];
      if (clientUid) {
        item = statementData.items.find((row) => row[CLIENT_LINE_ITEM_UUID_KEY] === clientUid);
      }
      if (
        !item &&
        itemIndex != null &&
        Number.isFinite(itemIndex) &&
        itemIndex >= 0 &&
        itemIndex < statementData.items.length
      ) {
        item = statementData.items[itemIndex];
      }
      if (!item) {
        item = statementData.items.find(
          (row) => normalizeLabel(row.label || row.line_item || '') === searchLabel
        );
      }
      if (item && item.risk_score !== undefined) {
        return {
          risk_score: item.risk_score,
          risk_category: item.risk_category,
          risk_factors: item.risk_factors,
          rationale: item.rationale
        };
      }
    }

    // Fall back to old nested structure for backward compatibility
    // Search through the nested structure for balance sheet
    if (statementType === 'balance_sheet') {
      // Check assets sections
      if (statementData.assets) {
        // Check current assets
        if (statementData.assets.current_assets) {
          const item = statementData.assets.current_assets.find(
            item => normalizeLabel(item.label) === searchLabel
          );
          if (item && item.risk_score !== undefined) {
            return {
              risk_score: item.risk_score,
              risk_category: item.risk_category
            };
          }
        }
        // Check non-current assets
        if (statementData.assets.non_current_assets) {
          const item = statementData.assets.non_current_assets.find(
            item => normalizeLabel(item.label) === searchLabel
          );
          if (item && item.risk_score !== undefined) {
            return {
              risk_score: item.risk_score,
              risk_category: item.risk_category
            };
          }
        }
      }

      // Check liabilities sections
      if (statementData.liabilities) {
        // Check current liabilities
        if (statementData.liabilities.current_liabilities) {
          const item = statementData.liabilities.current_liabilities.find(
            item => normalizeLabel(item.label) === searchLabel
          );
          if (item && item.risk_score !== undefined) {
            return {
              risk_score: item.risk_score,
              risk_category: item.risk_category
            };
          }
        }
        // Check non-current liabilities
        if (statementData.liabilities.non_current_liabilities) {
          const item = statementData.liabilities.non_current_liabilities.find(
            item => normalizeLabel(item.label) === searchLabel
          );
          if (item && item.risk_score !== undefined) {
            return {
              risk_score: item.risk_score,
              risk_category: item.risk_category
            };
          }
        }
      }

      // Check equity section (can be either 'equity' or 'equity.items')
      if (statementData.equity) {
        // Check if equity has an items array
        const equityItems = statementData.equity.items || statementData.equity;
        if (Array.isArray(equityItems)) {
          const item = equityItems.find(
            item => normalizeLabel(item.label) === searchLabel
          );
          if (item && item.risk_score !== undefined) {
            return {
              risk_score: item.risk_score,
              risk_category: item.risk_category
            };
          }
        }
      }
    }
    // Search for income statement items
    else if (statementType === 'income_statement') {
      // Check revenue items
      if (statementData.revenue) {
        const item = statementData.revenue.find(
          item => normalizeLabel(item.label || item.line_item || '') === searchLabel
        );
        if (item && item.risk_score !== undefined) {
          return {
            risk_score: item.risk_score,
            risk_category: item.risk_category,
            risk_factors: item.risk_factors,
            rationale: item.rationale
          };
        }
      }
      // Check expense items
      if (statementData.expenses) {
        const item = statementData.expenses.find(
          item => normalizeLabel(item.label || item.line_item || '') === searchLabel
        );
        if (item && item.risk_score !== undefined) {
          return {
            risk_score: item.risk_score,
            risk_category: item.risk_category,
            risk_factors: item.risk_factors,
            rationale: item.rationale
          };
        }
      }
    }

    return null;
  };

  const getRiskBadgeColor = (category) => {
    switch(category?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleRiskAssessment = async () => {
    if (!riskAssessmentDocIndices || riskAssessmentDocIndices.length === 0) {
      toast.error('Please select at least one document for Risk Assessment before continuing.');
      return;
    }

    setIsCalculatingRisk(true);
    setRiskAnalysisProgress('Initializing AI risk assessment...');

    try {

      // Check if we have multiple documents selected for risk assessment
      // allLineItemData indices are always [0, 1, 2, ...] regardless of original document selection
      // So we should always use sequential indices based on allLineItemData length
      const riskDocumentsToProcess = Array.from({ length: allLineItemData.length }, (_, i) => i);

      console.log('\n' + '='.repeat(80));
      console.log('🎯 RISK ASSESSMENT DOCUMENT SELECTION');
      console.log('='.repeat(80));
      console.log(`📋 Total documents in allLineItemData: ${allLineItemData.length}`);
      console.log(`📋 Documents to process: ${riskDocumentsToProcess.length}`);
      console.log(`📋 Risk documents indices: [${riskDocumentsToProcess.join(', ')}]`);
      console.log(`📋 Original user selections (riskAssessmentDocIndices): [${riskAssessmentDocIndices.join(', ')}]`);
      allLineItemData.forEach((doc, idx) => {
        console.log(`   ${idx}: ${doc.filename} (${doc.category})`);
      });
      console.log('='.repeat(80) + '\n');

      if (riskDocumentsToProcess.length === 0) {
        console.error('No documents to process for risk assessment');
        console.error('allLineItemData:', allLineItemData);
        console.error('uploadedFiles.length:', uploadedFiles.length);
        toast.error('No line items available for risk assessment. Please ensure documents are processed.');
        setIsCalculatingRisk(false);
        setRiskAnalysisProgress('');
        return;
      }

      const allRiskScores = [];

      await riskDocumentsToProcess.reduce(async (chain, docIndex, i) => {
        await chain;
        const currentDocData = allLineItemData[docIndex];

        console.log(`\n========== PROCESSING DOCUMENT ${i + 1} OF ${riskDocumentsToProcess.length} ==========`);
        console.log(`Document Index: ${docIndex}`);
        console.log(`Filename: ${currentDocData?.filename}`);
        console.log(`Category: ${currentDocData?.category}`);

        if (!currentDocData || !currentDocData.lineItems) {
          console.warn(`❌ No line items found for document index ${docIndex}`);
          return;
        }

        const documentLineItems = currentDocData.lineItems;
        const annualizationFactor = currentDocData.annualizationFactor || 1;

        setRiskAnalysisProgress(`[${i + 1}/${riskDocumentsToProcess.length}] Processing: ${currentDocData.filename}...`);

        const filteredLineItems = {};

        console.log(`📄 Line items structure for ${currentDocData.filename}:`, documentLineItems);

        if (documentLineItems?.balance_sheet) {
          const hasBalanceSheetContent = documentLineItems.balance_sheet.items &&
            Array.isArray(documentLineItems.balance_sheet.items) &&
            documentLineItems.balance_sheet.items.length > 0;

          if (hasBalanceSheetContent) {
            filteredLineItems.balance_sheet = {
              items: documentLineItems.balance_sheet.items.map(item => ({
                label: item.label,
                value: item.value,
                category: item.category,
                key: item.key || item.label.toLowerCase().replace(/\s+/g, '_'),
                units: item.units || 'USD',
                ...(item.isManualOverride && { isManualOverride: true })
              }))
            };
            console.log(`✅ Balance Sheet: ${filteredLineItems.balance_sheet.items.length} items`);
          } else {
            console.log('⚠️ Balance Sheet: No items found');
          }
        }

        if (documentLineItems?.income_statement) {
          const hasIncomeStatementContent = documentLineItems.income_statement.items &&
            Array.isArray(documentLineItems.income_statement.items) &&
            documentLineItems.income_statement.items.length > 0;

          if (hasIncomeStatementContent) {
            filteredLineItems.income_statement = {
              items: documentLineItems.income_statement.items.map(item => ({
                label: item.label,
                value: item.value,
                category: item.category,
                key: item.key || item.label.toLowerCase().replace(/\s+/g, '_'),
                units: item.units || 'USD',
                ...(item.isManualOverride && { isManualOverride: true })
              }))
            };
            console.log(`✅ Income Statement: ${filteredLineItems.income_statement.items.length} items (annualization factor: ${annualizationFactor})`);
          } else {
            console.log('⚠️ Income Statement: No items found');
          }
        }

        if (Object.keys(filteredLineItems).length === 0) {
          console.warn(`❌ No valid financial data available for document ${currentDocData.filename}`);
          return;
        }

        const requestData = {
          financial_data: filteredLineItems,
          materiality_thresholds: {
            overall_materiality: confirmedMateriality.overallMateriality,
            planning_materiality: confirmedMateriality.planningMateriality,
            de_minimis: confirmedMateriality.deminimis
          }
        };

        console.log(`\n${'='.repeat(80)}`);
        console.log(`📤 SENDING REQUEST FOR DOCUMENT ${i + 1}/${riskDocumentsToProcess.length}`);
        console.log(`   Filename: ${currentDocData.filename}`);
        console.log(`   Category: ${currentDocData.category}`);
        console.log(`${'='.repeat(80)}`);

        if (filteredLineItems.balance_sheet?.items) {
          console.log(`\n📊 BALANCE SHEET - Sending ${filteredLineItems.balance_sheet.items.length} items:`);
          filteredLineItems.balance_sheet.items.forEach((item, idx) => {
            console.log(`   ${idx + 1}. "${item.label}" = $${item.value?.toLocaleString()} (${item.category || 'N/A'})`);
          });
        } else {
          console.log(`\n📊 BALANCE SHEET - No items to send`);
        }

        if (filteredLineItems.income_statement?.items) {
          console.log(`\n📈 INCOME STATEMENT - Sending ${filteredLineItems.income_statement.items.length} items:`);
          filteredLineItems.income_statement.items.forEach((item, idx) => {
            console.log(`   ${idx + 1}. "${item.label}" = $${item.value?.toLocaleString()} (units: ${item.units})`);
          });
        } else {
          console.log(`\n📈 INCOME STATEMENT - No items to send`);
        }

        console.log(`\n🎯 MATERIALITY THRESHOLDS:`);
        console.log(`   Overall: $${confirmedMateriality.overallMateriality?.toLocaleString()}`);
        console.log(`   Planning: $${confirmedMateriality.planningMateriality?.toLocaleString()}`);
        console.log(`   De Minimis: $${confirmedMateriality.deminimis?.toLocaleString()}`);
        console.log(`${'='.repeat(80)}\n`);

        setRiskAnalysisProgress(`[${i + 1}/${riskDocumentsToProcess.length}] Evaluating ${currentDocData.filename}...`);

        try {
          console.log(`⏳ Waiting for response from document ${i + 1}...`);

          const response = await axiosInstance.post('/api/materiality/assess-risks', requestData);

          console.log(`✅ Response received for document ${i + 1}:`, response.data);

          if (response.data) {
            const riskData = response.data.risk_assessment || response.data;

            console.log(`📊 Risk scores received for document ${i + 1}:`, riskData);

            if (riskData.income_statement) {
              console.log(`   📋 Income statement scores structure:`, Object.keys(riskData.income_statement));

              let totalScored = 0;
              if (riskData.income_statement.revenue) {
                totalScored += riskData.income_statement.revenue.length;
                console.log(`      Revenue items scored: ${riskData.income_statement.revenue.length}`);
                riskData.income_statement.revenue.forEach((item, idx) => {
                  console.log(`         ${idx + 1}. ${item.line_item} - ${item.risk_category}`);
                });
              }
              if (riskData.income_statement.expenses) {
                totalScored += riskData.income_statement.expenses.length;
                console.log(`      Expense items scored: ${riskData.income_statement.expenses.length}`);
                riskData.income_statement.expenses.forEach((item, idx) => {
                  console.log(`         ${idx + 1}. ${item.line_item} - ${item.risk_category}`);
                });
              }
              console.log(`   Total income statement items scored: ${totalScored}`);
            }

            const balanceSheetScored = Object.keys(riskData.balance_sheet || {}).length;
            console.log(`   Balance sheet sections: ${balanceSheetScored}`);

            allRiskScores.push({
              documentIndex: docIndex,
              filename: currentDocData.filename,
              category: currentDocData.category,
              riskScores: {
                balance_sheet: riskData.balance_sheet || {},
                income_statement: riskData.income_statement || {},
                summary: riskData.summary || {}
              }
            });

            console.log(`✅ Risk scores stored for document ${i + 1} (${currentDocData.filename})`);
            setRiskAnalysisProgress(`[${i + 1}/${riskDocumentsToProcess.length}] Completed: ${currentDocData.filename}`);
          }
        } catch (docError) {
          console.error(`❌ Error processing document ${i + 1} (${currentDocData.filename}):`, docError);
          if (docError?.response?.status === 403) {
            alert(resolve403ErrorMessage(docError, 'Failed to perform risk assessment. Please try again.'));
            setIsCalculatingRisk(false);
            setRiskAnalysisProgress('');
            return;
          }
          return;
        }

        console.log(`========== COMPLETED DOCUMENT ${i + 1} OF ${riskDocumentsToProcess.length} ==========\n`);
      }, Promise.resolve());

      // After processing all documents
      if (allRiskScores.length === 0) {
        console.error('❌ Risk assessment failed - no scores returned from API');
        toast.error('Risk assessment failed. Please check that your documents contain valid financial data and try again.');
        setIsCalculatingRisk(false);
        setRiskAnalysisProgress('');
        // Keep riskScores as null so user can retry
        return;
      }

      setRiskAnalysisProgress('Finalizing risk assessment results for all documents...');

      // Store ALL risk scores separately - DO NOT MERGE
      // Each document maintains its own risk scores
      console.log('\n' + '='.repeat(80));
      console.log('📊 STORING RISK SCORES FOR ALL DOCUMENTS');
      console.log('='.repeat(80));

      allRiskScores.forEach((docRisk, index) => {
        console.log(`\n📄 Document ${index + 1}: ${docRisk.filename}`);
        console.log(`   Document Index: ${docRisk.documentIndex}`);
        console.log(`   Revenue items scored: ${docRisk.riskScores.income_statement?.revenue?.length || 0}`);
        console.log(`   Expense items scored: ${docRisk.riskScores.income_statement?.expenses?.length || 0}`);
        console.log(`   Balance sheet sections: ${Object.keys(docRisk.riskScores.balance_sheet || {}).length}`);
      });

      console.log('='.repeat(80) + '\n');

      let scoresToStore;
      if (allRiskScores.length === 1) {
        console.log('Single document processed - storing risk scores directly');
        scoresToStore = allRiskScores[0].riskScores;
      } else {
        console.log('Multiple documents processed - storing risk scores by index');
        scoresToStore = {};
        allRiskScores.forEach((docRisk) => {
          scoresToStore[docRisk.documentIndex] = docRisk.riskScores;
        });
      }
      const riskScoresWithClientIds = cloneMaterialityRiskScores(scoresToStore);
      syncClientUuidsFromLineItemsOntoRiskScoresRoot(allLineItemData, riskScoresWithClientIds);
      setRiskScores(riskScoresWithClientIds);

      // Log sample data to debug structure
      const firstDocScores = allRiskScores.length === 1
        ? allRiskScores[0].riskScores
        : allRiskScores[0]?.riskScores;

      if (firstDocScores) {
        console.log('Sample risk scores structure:', {
          balance_sheet_sections: Object.keys(firstDocScores.balance_sheet || {}),
          income_statement_sections: Object.keys(firstDocScores.income_statement || {}),
          summary: firstDocScores.summary
        });

        if (firstDocScores?.balance_sheet?.assets?.current_assets?.[0]) {
          console.log('Sample current asset with risk:', firstDocScores.balance_sheet.assets.current_assets[0]);
        }
        if (firstDocScores?.income_statement?.revenue?.[0]) {
          console.log('Sample revenue item with risk:', firstDocScores.income_statement.revenue[0]);
        }
      }

      console.log(`Successfully completed risk assessment for ${allRiskScores.length} document(s)`);

      // Stay on current step to allow editing
      // setCurrentStep(4);  // Commented out - user stays on step 3
    } catch (error) {
      console.error('Error performing risk assessment:', error);
      toast.error('Failed to perform risk assessment. Please try again.');
    } finally {
      setIsCalculatingRisk(false);
      setRiskAnalysisProgress('');
    }
  };

  // Function to save materiality assessment to backend
  const authenticatedUser = useAuthUser(user);
  const handleSaveAssessment = async () => {
    if (!versionName.trim()) {
      toast.error('Please enter a version name');
      return;
    }

    setIsSavingAssessment(true);

    try {
      const hasUser = authenticatedUser && (authenticatedUser.id != null || authenticatedUser.username);
      if (!hasUser) {
        toast.error('User session not found. Please refresh and try again.');
        setIsSavingAssessment(false);
        return;
      }
      // Prepare documents JSON
      const documentsJSON = documentAnalysisResults && documentAnalysisResults.length > 0 ? {
        uploadedFiles: uploadedFiles.map((f, i) => ({
          name: f.name,
          size: f.size,
          type: f.type,
          file_id: uploadedFileEntries[i]?.file_id || uploadedFileEntries[i]?.uuid || null
        })),
        analysisResults: documentAnalysisResults,
        selectedForMateriality: selectedDocumentIndices,
        selectedForRisk: riskAssessmentDocIndices
      } : null;

      // Get the sensitivity percentage
      const getSensitivityPercentage = () => {
        // Check if selected category is a custom category
        const customCategory = customCategories.find(cat => cat.name === selectedCategory);

        if (customCategory) {
          const basePercentage = customCategory.percentage;
          if (sensitivityLevel === 'Conservative') return `${(basePercentage / 2).toFixed(1)}%`;
          if (sensitivityLevel === 'Moderate') return `${(basePercentage * 2).toFixed(1)}%`;
          return `${basePercentage.toFixed(1)}%`; // Standard
        }

        // For standard categories
        if (selectedCategory === 'Net Income') {
          if (sensitivityLevel === 'Conservative') return '2.5%';
          if (sensitivityLevel === 'Moderate') return '7.5%';
          return '5.0%'; // Standard
        } else if (selectedCategory === 'Total Revenue') {
          if (sensitivityLevel === 'Conservative') return '0.5%';
          if (sensitivityLevel === 'Moderate') return '2.0%';
          return '1.0%'; // Standard
        } else if (selectedCategory === 'Total Assets') {
          if (sensitivityLevel === 'Conservative') return '0.5%';
          if (sensitivityLevel === 'Moderate') return '2.0%';
          return '1.0%'; // Standard
        }
        return '5.0%'; // Default
      };

      // Prepare materiality JSON
      const materialityJSON = {
        companyProfile: companyProfile,
        documentInfo: documentInfo,
        selectedCategory: selectedCategory,
        sensitivityLevel: sensitivityLevel,
        sensitivityPercentage: getSensitivityPercentage(),
        confirmedMateriality: confirmedMateriality,
        materialityRecommendation: materialityRecommendation,
        recommendationReasoning: recommendationReasoning,
        recommendationConfidence: recommendationConfidence,
        overrideAmount: overrideAmount,
        overrideReasoning: overrideReasoning,
        customCategories: customCategories
      };

      // Prepare SOX Cycle JSON with line items and risk scores
      // If multiple documents exist, only save the active document's data
      const isMultiDocSave = allLineItemData.length > 1;
      const activeRiskScores = getRiskScoresForActiveDocument(
        riskScores,
        activeLineItemIndex,
        isMultiDocSave
      );

      // Merge UI line items into risk payload so edited labels/values/categories match the screen
      const rawAnnualizationFactor = Number(allLineItemData?.[activeLineItemIndex]?.annualizationFactor);
      const saveAnnualizationFactor =
        Number.isFinite(rawAnnualizationFactor) && rawAnnualizationFactor > 0 ? rawAnnualizationFactor : 1;
      const riskScoresWithNotes =
        buildMergedRiskScoresForSave(
          lineItemData,
          activeRiskScores,
          activeLineItemIndex,
          lineItemNotes,
          saveAnnualizationFactor,
          (item, statementType) => {
            const cycles = getSoxCycles(
              item?.label || item?.line_item || '',
              item,
              statementType
            );
            return Array.isArray(cycles) ? cycles : [];
          }
        ) ?? (activeRiskScores ? cloneDeep(activeRiskScores) : null);

      const riskScoresForApi = riskScoresWithNotes
        ? stripClientLineItemUuidDeep(riskScoresWithNotes)
        : null;
      let lineItemsForApi = null;
      if (lineItemData) {
        lineItemsForApi = stripClientLineItemUuidDeep(cloneDeep(lineItemData));
        if (Array.isArray(lineItemsForApi?.balance_sheet?.items)) {
          lineItemsForApi.balance_sheet.items = lineItemsForApi.balance_sheet.items.map((item) => {
            let resolvedSoxCycles;
            if (Array.isArray(item?.sox_cycles) && item.sox_cycles.length > 0) {
              resolvedSoxCycles = item.sox_cycles;
            } else if (item?.sox_cycle) {
              resolvedSoxCycles = [item.sox_cycle];
            } else {
              resolvedSoxCycles = getSoxCycles(item?.label, item, 'balance_sheet');
            }
            return {
              ...item,
              sox_cycles: resolvedSoxCycles,
              sox_cycle: item?.sox_cycle ?? resolvedSoxCycles?.[0] ?? null,
            };
          });
        }
        if (Array.isArray(lineItemsForApi?.income_statement?.items)) {
          lineItemsForApi.income_statement.items = lineItemsForApi.income_statement.items.map((item) => {
            let resolvedSoxCycles;
            if (Array.isArray(item?.sox_cycles) && item.sox_cycles.length > 0) {
              resolvedSoxCycles = item.sox_cycles;
            } else if (item?.sox_cycle) {
              resolvedSoxCycles = [item.sox_cycle];
            } else {
              resolvedSoxCycles = getSoxCycles(item?.label || item?.line_item, item, 'income_statement');
            }
            return {
              ...item,
              sox_cycles: resolvedSoxCycles,
              sox_cycle: item?.sox_cycle ?? resolvedSoxCycles?.[0] ?? null,
            };
          });
        }
        if (saveAnnualizationFactor > 1 && Array.isArray(lineItemsForApi?.income_statement?.items)) {
          lineItemsForApi.income_statement.items = lineItemsForApi.income_statement.items.map((item) => ({
            ...item,
            value: (Number(item?.value) || 0) * saveAnnualizationFactor,
          }));
        }
      }
      const lineItemNotesForApi = serializeLineItemNotesForApi(
        lineItemNotes,
        lineItemData,
        activeLineItemIndex
      );

      const soxCycleJSON = {
        riskScores: riskScoresForApi,
        lineItems: lineItemsForApi,
        customRiskLevels: customRiskLevels,
        customSoxCycles: customSoxCycles,
        lineItemNotes: lineItemNotesForApi
      };

      // Prepare entity scoping JSON (if user went through entity scoping)
      const entityScopingJSON = entityScopingDataForSave || (trialBalanceFile ? {
        trialBalanceFile: { name: trialBalanceFile.name, size: trialBalanceFile.size },
        glEntriesData: glEntriesData,
        thresholdOverride: thresholdOverride,
        thresholdJustification: thresholdJustification
      } : null);

      // Prepare the request payload
      const payload = {
        analysis_name: versionName.trim(),
        documents_json: documentsJSON,
        materiality_json: materialityJSON,
        sox_cycle_json: soxCycleJSON,
        entityscoping_json: entityScopingJSON,
        ...(selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {}),
      };

      console.log('=== SAVING ASSESSMENT ===');
      console.log('Analysis Name:', payload.analysis_name);
      console.log('Active Document Index:', activeLineItemIndex);
      console.log('Total Documents:', allLineItemData.length);
      console.log('Documents JSON:', payload.documents_json ? 'Present' : 'NULL');
      console.log('Materiality JSON:', payload.materiality_json);
      console.log('SOX Cycle JSON:', payload.sox_cycle_json);
      console.log('Entity Scoping JSON:', payload.entityscoping_json);
      console.log('Full Payload:', JSON.stringify(payload, null, 2));

      // Send to backend
      const response = await axiosInstance.post('/api/materiality-assessments', payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Backend response:', response.data);

      if (response.data.success || response.data.id) {
        console.log('Assessment saved successfully:', response.data);

        // Store the assessment ID for reference
        localStorage.setItem('lastAssessmentId', response.data.id);
        localStorage.setItem('lastAssessmentName', versionName);

        // Close modal and move to success step
        setShowVersionModal(false);
        setCurrentStep(8);
      } else {
        console.error('Unexpected response structure:', response.data);
        throw new Error('Failed to save assessment');
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(`Failed to save assessment: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
    } finally {
      setIsSavingAssessment(false);
    }
  };

  // Function to determine risk category based on score
  const getRiskCategory = (score) => {
    if (!score || score === 0) return '';
    const numScore = parseFloat(score);
    if (numScore <= 3.3) return 'LOW';
    if (numScore <= 6.6) return 'MODERATE';
    return 'HIGH';
  };

  // Component to render clickable risk score
  const RiskScoreDisplay = ({ risk, statementType, sectionPath, itemIndex, itemLabel }) => {
    if (!risk) return null;

    const currentScore = risk.risk_score || risk.corporate_risk_score || 0;
    const currentCategory = risk.risk_level || risk.risk_category || getRiskCategory(currentScore);

    const handleClick = () => {
      if (!riskScores) {
        toast.error('Please perform risk assessment first');
        return;
      }

      setEditingRiskScore({ statementType, sectionPath, itemIndex, itemLabel, currentScore });
    };

    return (
      <button
        onClick={handleClick}
        className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getRiskBadgeColor(currentCategory)} hover:opacity-80 hover:scale-105 cursor-pointer transition-all duration-200`}
        title="Click to edit risk score"
      >
        {currentCategory} ({currentScore.toFixed(1)})
      </button>
    );
  };

  // Component for risk score edit modal with slider
  const RiskScoreEditModal = React.memo(() => {
    // Use local state for the temp score to avoid parent re-renders
    const [localTempScore, setLocalTempScore] = React.useState('');

    // Initialize local score when modal opens
    React.useEffect(() => {
      if (editingRiskScore) {
        setLocalTempScore(editingRiskScore.currentScore.toString());
      }
    }, []);
    // Add keyboard event listener for ESC key - must be before conditional return
    React.useEffect(() => {
      if (!editingRiskScore) return;

      const handleEscKey = (e) => {
        if (e.key === 'Escape') {
          setEditingRiskScore(null);
        }
      };

      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }, []);

    if (!editingRiskScore) {
      return null;
    }

    // Original score that's currently saved
    const originalScore = editingRiskScore.currentScore || 5.0;
    const originalCategory = getRiskCategory(originalScore);

    // New score being edited (not yet saved)
    const newScore = parseFloat(localTempScore) || originalScore;
    const newCategory = getRiskCategory(newScore);

    const handleSliderChange = (e) => {
      const newValue = e.target.value;
      setLocalTempScore(newValue);  // Update local state only, not parent state
    };

    const handleSave = () => {
      const score = parseFloat(localTempScore);
      if (isNaN(score) || score < 1.0 || score > 10.0) {
        toast.error('Please enter a valid score between 1.0 and 10.0');
        return;
      }

      // Save the score changes
      handleRiskScoreEdit(
        editingRiskScore.statementType,
        editingRiskScore.sectionPath,
        editingRiskScore.itemIndex,
        score,
        editingRiskScore.itemLabel
      );

      // Close the modal after saving
      setEditingRiskScore(null);
    };

    const handleClose = () => {
      setEditingRiskScore(null);
    };

    const handleBackdropClick = (e) => {
      // Close modal if clicking on the backdrop (not the modal content)
      if (e.target === e.currentTarget) {
        handleClose();
      }
    };

    // Get category color for display
    const getCategoryColor = (category) => {
      switch(category) {
        case 'HIGH': return 'text-red-600 bg-red-100';
        case 'MODERATE': return 'text-yellow-600 bg-yellow-100';
        case 'LOW': return 'text-green-600 bg-green-100';
        default: return 'text-gray-600 bg-gray-100';
      }
    };

    // Get slider track gradient colors
    const getSliderBackground = () => {
      return `linear-gradient(to right,
        rgb(34, 197, 94) 0%,
        rgb(34, 197, 94) 33%,
        rgb(250, 204, 21) 33%,
        rgb(250, 204, 21) 67%,
        rgb(239, 68, 68) 67%,
        rgb(239, 68, 68) 100%)`;
    };

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-xl shadow-2xl p-6 w-[450px] relative animate-slideUp">
          {/* Close button (X) */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Editing Risk Score for {editingRiskScore.itemLabel}
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Current Saved Score:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(originalCategory)}`}>
                {originalScore.toFixed(1)} - {originalCategory}
              </span>
            </div>
          </div>

          {/* Slider Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-medium text-gray-700">
                Adjust Risk Score
              </label>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">New:</span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(newCategory)}`}>
                  {newCategory}
                </span>
                <span className="text-2xl font-bold text-gray-900">
                  {newScore.toFixed(1)}
                </span>
                {newScore !== originalScore && (
                  <span className="text-sm text-gray-500">
                    (was {originalScore.toFixed(1)})
                  </span>
                )}
              </div>
            </div>

            {/* Slider */}
            <div className="relative">
              <input
                type="range"
                min="1.0"
                max="10.0"
                step="0.1"
                value={localTempScore}
                onChange={handleSliderChange}
                className="w-full risk-slider"
                style={{
                  background: getSliderBackground(),
                }}
              />

              {/* Tick marks and labels */}
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>1.0</span>
                <span>3.3</span>
                <span>6.6</span>
                <span>10.0</span>
              </div>
            </div>

            {/* Category indicators */}
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className={`text-center py-2 rounded ${newCategory === 'LOW' ? 'bg-green-100 border-2 border-green-500' : 'bg-gray-50'}`}>
                <div className={`text-xs font-medium ${newCategory === 'LOW' ? 'text-green-700' : 'text-gray-500'}`}>
                  LOW
                </div>
                <div className={`text-xs ${newCategory === 'LOW' ? 'text-green-600' : 'text-gray-400'}`}>
                  1.0 - 3.3
                </div>
              </div>
              <div className={`text-center py-2 rounded ${newCategory === 'MODERATE' ? 'bg-yellow-100 border-2 border-yellow-500' : 'bg-gray-50'}`}>
                <div className={`text-xs font-medium ${newCategory === 'MODERATE' ? 'text-yellow-700' : 'text-gray-500'}`}>
                  MODERATE
                </div>
                <div className={`text-xs ${newCategory === 'MODERATE' ? 'text-yellow-600' : 'text-gray-400'}`}>
                  3.4 - 6.6
                </div>
              </div>
              <div className={`text-center py-2 rounded ${newCategory === 'HIGH' ? 'bg-red-100 border-2 border-red-500' : 'bg-gray-50'}`}>
                <div className={`text-xs font-medium ${newCategory === 'HIGH' ? 'text-red-700' : 'text-gray-500'}`}>
                  HIGH
                </div>
                <div className={`text-xs ${newCategory === 'HIGH' ? 'text-red-600' : 'text-gray-400'}`}>
                  6.7 - 10.0
                </div>
              </div>
            </div>

            {/* Info text */}
            <div className="text-xs text-gray-500 text-center mt-2">
              Drag the slider to adjust the risk score. Changes will only be saved when you click "Save Changes".
            </div>

            {/* Quick preset buttons */}
            <div className="flex justify-center gap-2 mt-4">
              <button
                onClick={() => setLocalTempScore('2.0')}
                className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
              >
                Low (2.0)
              </button>
              <button
                onClick={() => setLocalTempScore('5.0')}
                className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200 transition-colors"
              >
                Moderate (5.0)
              </button>
              <button
                onClick={() => setLocalTempScore('8.0')}
                className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors"
              >
                High (8.0)
              </button>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={newScore === originalScore}
            >
              {newScore === originalScore ? 'No Changes' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  });

  // Function to handle risk score edit for a specific line item
  const handleRiskScoreEdit = (statementType, sectionPath, itemIndex, newScore, itemLabel = null) => {
    // Validate score is between 1.0 and 10.0
    const score = parseFloat(newScore);
    if (isNaN(score) || score < 1.0 || score > 10.0) {
      return; // Invalid score
    }

    // Deep clone the risk scores
    // For multiple documents, riskScores is indexed by document index
    // For single document, riskScores is the direct object
    let updatedScores;
    const isMultipleDocuments = allLineItemData.length > 1;

    if (isMultipleDocuments) {
      updatedScores = cloneDeep(riskScores);
      if (!updatedScores[activeLineItemIndex]) {
        console.error(`No risk scores found for document index ${activeLineItemIndex}`);
        return;
      }
    } else {
      updatedScores = cloneDeep(riskScores);
    }

    // Navigate to the correct item and update its score
    // Get the document-specific scores object
    const docScores = isMultipleDocuments ? updatedScores[activeLineItemIndex] : updatedScores;

    let targetItem;
    const normalizeLabel = (label) => label?.toLowerCase().trim();
    const searchLabel = normalizeLabel(itemLabel);

    // Check for v2 flat structure first (with items array)
    if (docScores[statementType]?.items && Array.isArray(docScores[statementType].items)) {
      console.log(`Using v2 flat structure for ${statementType}`);
      const items = docScores[statementType].items;
      const lineRows = lineItemData?.[statementType]?.items;
      const clientUid =
        lineRows &&
        itemIndex != null &&
        Number.isFinite(itemIndex) &&
        itemIndex >= 0 &&
        itemIndex < lineRows.length
          ? lineRows[itemIndex][CLIENT_LINE_ITEM_UUID_KEY]
          : null;
      let item = null;
      if (clientUid) {
        item = items.find((row) => row[CLIENT_LINE_ITEM_UUID_KEY] === clientUid);
      }
      if (
        !item &&
        itemIndex != null &&
        Number.isFinite(itemIndex) &&
        itemIndex >= 0 &&
        itemIndex < items.length
      ) {
        item = items[itemIndex];
      }
      if (!item) {
        item = items.find(
          (row) => normalizeLabel(row.label || row.line_item) === searchLabel
        );
      }
      if (item) {
        item.risk_score = score;
        item.risk_category = getRiskCategory(score);
        targetItem = item;
        console.log(`Updated v2 item: ${item.label} to score ${score} (Document ${isMultipleDocuments ? activeLineItemIndex : 'single'})`);
      }
    } else {
      // Fall back to old nested structure
      if (statementType === 'balance_sheet') {
        const pathParts = sectionPath.split('.');
        if (pathParts.length === 2) {
          // e.g., 'assets.current_assets' or 'equity.items'
          const [section, subsection] = pathParts;
          if (docScores.balance_sheet?.[section]?.[subsection]?.[itemIndex]) {
            targetItem = docScores.balance_sheet[section][subsection][itemIndex];
            targetItem.risk_score = score;
            targetItem.risk_category = getRiskCategory(score);
          }
        }
      } else if (statementType === 'income_statement') {
        // For income statement, find the item by label in either revenue or expenses arrays
        // Check revenue items
        if (docScores.income_statement?.revenue) {
          const revenueItem = docScores.income_statement.revenue.find(
            item => normalizeLabel(item.label) === searchLabel
          );
          if (revenueItem) {
            revenueItem.risk_score = score;
            revenueItem.risk_category = getRiskCategory(score);
            targetItem = revenueItem;
          }
        }

        // Check expense items if not found in revenue
        if (!targetItem && docScores.income_statement?.expenses) {
          const expenseItem = docScores.income_statement.expenses.find(
            item => normalizeLabel(item.label) === searchLabel
          );
          if (expenseItem) {
            expenseItem.risk_score = score;
            expenseItem.risk_category = getRiskCategory(score);
            targetItem = expenseItem;
          }
        }
      }
    }

    // Update the summary statistics for the active document
    if (docScores.summary) {
      let totalScore = 0;
      let totalItems = 0;
      let highCount = 0;
      let moderateCount = 0;
      let lowCount = 0;

      // Helper function to count items
      const countItems = (items) => {
        if (Array.isArray(items)) {
          items.forEach(item => {
            if (item.risk_score) {
              totalScore += item.risk_score;
              totalItems++;
              const cat = item.risk_category;
              if (cat === 'HIGH') highCount++;
              else if (cat === 'MODERATE') moderateCount++;
              else if (cat === 'LOW') lowCount++;
            }
          });
        }
      };

      // Count balance sheet items (v2 flat structure)
      if (docScores.balance_sheet?.items && Array.isArray(docScores.balance_sheet.items)) {
        countItems(docScores.balance_sheet.items);
      } else if (docScores.balance_sheet) {
        // Count balance sheet items (old nested structure)
        ['assets', 'liabilities', 'equity'].forEach(section => {
          if (docScores.balance_sheet[section]) {
            const subsections = section === 'equity' ? ['items'] :
                               section === 'assets' ? ['current_assets', 'non_current_assets'] :
                               ['current_liabilities', 'non_current_liabilities'];

            subsections.forEach(subsection => {
              countItems(docScores.balance_sheet[section][subsection]);
            });
          }
        });
      }

      // Count income statement items (v2 flat structure)
      if (docScores.income_statement?.items && Array.isArray(docScores.income_statement.items)) {
        countItems(docScores.income_statement.items);
      } else if (docScores.income_statement) {
        // Count income statement items (old nested structure)
        ['revenue', 'expenses'].forEach(section => {
          countItems(docScores.income_statement[section]);
        });
      }

      docScores.summary.total_items = totalItems;
      docScores.summary.high_risk_count = highCount;
      docScores.summary.moderate_risk_count = moderateCount;
      docScores.summary.low_risk_count = lowCount;
      docScores.summary.average_risk_score = totalItems > 0 ? totalScore / totalItems : 0;
    }

    setRiskScores(updatedScores);
  };

  // Function to handle adding a new line item
  const handleAddLineItem = (statementType, category = null) => {
    const hasRiskAssessment = !!getRiskScoresForActiveDocument(
      riskScores,
      activeLineItemIndex,
      allLineItemData.length > 1
    );
    if (!newLineItem.label || !newLineItem.value) {
      toast.error('Please fill in all fields: Line Item Name and Value');
      return;
    }
    if (hasRiskAssessment && !newLineItem.riskScore) {
      toast.error('Please enter a Risk Score (1.0 to 10.0).');
      return;
    }

    const value = parseFloat(newLineItem.value);
    if (isNaN(value)) {
      toast.error('Please enter a valid numeric value.');
      return;
    }

    let resolvedCategory;
    if (statementType === 'balance_sheet') {
      const sel = newLineItem.balanceSheetCategory || 'asset';
      if (!BALANCE_SHEET_LINE_CATEGORIES.includes(sel)) {
        toast.error('Please select a balance sheet category: Asset, Liability, or Equity.');
        return;
      }
      resolvedCategory = sel;
    } else if (statementType === 'income_statement') {
      const sel = newLineItem.incomeStatementCategory || 'revenue';
      if (!INCOME_STATEMENT_LINE_CATEGORIES.includes(sel)) {
        toast.error('Please select an income statement category: Revenue or Expense.');
        return;
      }
      resolvedCategory = sel;
    } else {
      resolvedCategory = category || 'other';
    }

    const docIdx = activeLineItemIndex;
    const lineItemsSnapshot =
      Array.isArray(allLineItemData) &&
      docIdx >= 0 &&
      docIdx < allLineItemData.length &&
      allLineItemData[docIdx]?.lineItems
        ? allLineItemData[docIdx].lineItems
        : lineItemData;

    if (!lineItemsSnapshot || typeof lineItemsSnapshot !== 'object') {
      toast.error('No line item data to add to.');
      return;
    }

    const updatedLineItemData = cloneDeep(lineItemsSnapshot);

    const updatedRiskScores = riskScores ? cloneDeep(riskScores) : null;

    const defaultSoxCycles = getSoxCycles(newLineItem.label.trim(), { category: resolvedCategory, value }, statementType);
    const newItemCore = {
      category: resolvedCategory,
      label: newLineItem.label.trim(),
      units: 'USD',
      value: value,
      sox_cycles: defaultSoxCycles,
      sox_cycle: defaultSoxCycles?.[0] ?? null,
      custom_added: true
    };
    const newItemForLineItems = ensureClientLineItemUuidOnItem(newItemCore);
    const newUid = newItemForLineItems[CLIENT_LINE_ITEM_UUID_KEY];
    const newItemForRiskScores = newUid ? { ...newItemCore, [CLIENT_LINE_ITEM_UUID_KEY]: newUid } : { ...newItemCore };
    const trimmedNote = String(newLineItem.note || '').trim();

    // Add to line item data
    if (statementType === 'income_statement') {
      if (!updatedLineItemData.income_statement) {
        updatedLineItemData.income_statement = { items: [], period: 'Custom' };
      }
      if (!updatedLineItemData.income_statement.items) {
        updatedLineItemData.income_statement.items = [];
      }
      updatedLineItemData.income_statement.items.push(newItemForLineItems);
    } else if (statementType === 'balance_sheet') {
      if (!updatedLineItemData.balance_sheet) {
        updatedLineItemData.balance_sheet = { items: [], period: 'Custom' };
      }
      if (!updatedLineItemData.balance_sheet.items) {
        updatedLineItemData.balance_sheet.items = [];
      }
      updatedLineItemData.balance_sheet.items.push(newItemForLineItems);
    }

    const isMultiDoc = allLineItemData.length > 1;

    // Add to risk scores only if assessment data already exists.
    // Pre-assessment rows should be sent to assess-risks API with no local/manual score.
    if (updatedRiskScores) {
      const parsedRiskScore = Number.parseFloat(newLineItem.riskScore);
      const hasManualRiskScore = Number.isFinite(parsedRiskScore) && parsedRiskScore >= 1.0 && parsedRiskScore <= 10.0;
      if (hasRiskAssessment && !hasManualRiskScore) {
        toast.error('Please enter a valid Risk Score between 1.0 and 10.0.');
        return;
      }
      let targetScores;
      if (isMultiDoc) {
        if (!updatedRiskScores[activeLineItemIndex]) {
          updatedRiskScores[activeLineItemIndex] = {
            balance_sheet: { items: [] },
            income_statement: { items: [] },
            summary: {
              total_items: 0,
              high_risk_count: 0,
              moderate_risk_count: 0,
              low_risk_count: 0,
              average_risk_score: 0,
            },
          };
        }
        targetScores = updatedRiskScores[activeLineItemIndex];
      } else {
        targetScores = updatedRiskScores;
      }

      const itemWithRisk = hasManualRiskScore
        ? {
          ...newItemForRiskScores,
          risk_score: parsedRiskScore,
          risk_category: getRiskCategory(parsedRiskScore),
        }
        : null;

      if (hasManualRiskScore && statementType === 'income_statement') {
        if (!targetScores.income_statement) {
          targetScores.income_statement = { items: [] };
        }
        if (!targetScores.income_statement.items) {
          targetScores.income_statement.items = [];
        }
        targetScores.income_statement.items.push(itemWithRisk);
      } else if (hasManualRiskScore && statementType === 'balance_sheet') {
        if (!targetScores.balance_sheet) {
          targetScores.balance_sheet = { items: [] };
        }
        if (!targetScores.balance_sheet.items) {
          targetScores.balance_sheet.items = [];
        }
        targetScores.balance_sheet.items.push(itemWithRisk);
      }

      if (hasManualRiskScore && targetScores.summary) {
        const cat = itemWithRisk.risk_category;
        targetScores.summary.total_items = (targetScores.summary.total_items || 0) + 1;
        if (cat === 'HIGH') {
          targetScores.summary.high_risk_count = (targetScores.summary.high_risk_count || 0) + 1;
        } else if (cat === 'MODERATE') {
          targetScores.summary.moderate_risk_count = (targetScores.summary.moderate_risk_count || 0) + 1;
        } else if (cat === 'LOW') {
          targetScores.summary.low_risk_count = (targetScores.summary.low_risk_count || 0) + 1;
        }

        let totalScore = 0;
        let totalItems = 0;
        const countItems = (items) => {
          if (Array.isArray(items)) {
            items.forEach((item) => {
              if (item.risk_score) {
                totalScore += item.risk_score;
                totalItems++;
              }
            });
          }
        };

        if (targetScores.balance_sheet?.items) countItems(targetScores.balance_sheet.items);
        if (targetScores.income_statement?.items) countItems(targetScores.income_statement.items);

        targetScores.summary.average_risk_score = totalItems > 0 ? totalScore / totalItems : 0;
      }

      setRiskScores(updatedRiskScores);
    }

    setLineItemData(updatedLineItemData);
    setAllLineItemData((prev) => {
      if (!Array.isArray(prev)) return prev;
      const i = activeLineItemIndex;
      if (prev.length === 0) {
        return [
          {
            lineItems: updatedLineItemData,
            filename: documentAnalysisResults[0]?.filename || '',
            category: documentAnalysisResults[0]?.category || '',
            period: documentAnalysisResults[0]?.period || '',
            documentIndex: 0,
            annualizationFactor: 1,
            periodMonths: 12,
          },
        ];
      }
      if (i < 0 || i >= prev.length) return prev;
      const next = [...prev];
      next[i] = { ...next[i], lineItems: updatedLineItemData };
      return next;
    });
    if (trimmedNote && newUid) {
      setLineItemNotes((prev) => ({ ...prev, [newUid]: trimmedNote }));
    }
    setNewLineItem({
      label: '',
      value: '',
      riskScore: '',
      note: '',
      balanceSheetCategory: 'asset',
      incomeStatementCategory: 'revenue'
    });
    setShowAddLineItemForm({ show: false, statementType: null, category: null });
  };

  /** Clear step-3 manual overrides so thresholds follow the selected category and plan (Conservative / Standard / Moderate). */
  const resetMaterialityToPlanDefaults = useMemo(
    () => () => {
      setCustomOverallMateriality('');
      setCustomPlanningMateriality('');
      setCustomDeminimis('');
      setOverrideAmount('');
      setOverrideReasoning('');
      setShowReasoningBox(false);
    },
    []
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderFiscalYearStep();
      case 2:
        return renderUploadStep();
      case 3:
        return (
          <AnalyzeDocuments
            isProcessing={isProcessing}
            documentAnalysisResults={documentAnalysisResults}
            selectedDocumentIndices={selectedDocumentIndices}
            setSelectedDocumentIndices={setSelectedDocumentIndices}
            riskAssessmentDocIndices={riskAssessmentDocIndices}
            setRiskAssessmentDocIndices={setRiskAssessmentDocIndices}
            showDifferentYearWarning={showDifferentYearWarning}
            detectedMismatchYear={detectedMismatchYear}
            selectedAnalysisYear={selectedAnalysisYear}
          />
        );
      case 4:
        return (
          <MaterialityAnalysis
            isProcessing={isProcessing}
            analysisError={analysisError}
            onDismissError={() => setAnalysisError(null)}
            companyProfile={companyProfile}
            documentInfo={documentInfo}
            activeDocumentIndex={activeDocumentIndex}
            handleDocumentSwitch={handleDocumentSwitch}
            isUpdatingProfile={isUpdatingProfile}
            materialityRecommendation={materialityRecommendation}
            recommendationReasoning={recommendationReasoning}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            sensitivityLevel={sensitivityLevel}
            setSensitivityLevel={setSensitivityLevel}
            overrideAmount={overrideAmount}
            setOverrideAmount={setOverrideAmount}
            overrideReasoning={overrideReasoning}
            setOverrideReasoning={setOverrideReasoning}
            customOverallMateriality={customOverallMateriality}
            setCustomOverallMateriality={setCustomOverallMateriality}
            customPlanningMateriality={customPlanningMateriality}
            setCustomPlanningMateriality={setCustomPlanningMateriality}
            customDeminimis={customDeminimis}
            setCustomDeminimis={setCustomDeminimis}
            showReasoningBox={showReasoningBox}
            setShowReasoningBox={setShowReasoningBox}
            customCategories={customCategories}
            setCustomCategories={setCustomCategories}
            calculateMateriality={calculateMateriality}
            formatCurrency={formatCurrency}
            analysisProgress={analysisProgress}
            analysisSteps={analysisSteps}
            getSensitivityOptions={getSensitivityOptions}
            onResetMaterialityToPlanDefaults={resetMaterialityToPlanDefaults}
          />
        );
      case 5:
        return (
          <RiskAssessment
            isLoadingLineItems={isLoadingLineItems}
            lineItemData={lineItemData}
            confirmedMateriality={confirmedMateriality}
            formatCurrency={formatCurrency}
            handleRiskAssessment={handleRiskAssessment}
            isCalculatingRisk={isCalculatingRisk}
            riskScores={riskScores}
            riskAnalysisProgress={riskAnalysisProgress}
            allLineItemData={allLineItemData}
            activeLineItemIndex={activeLineItemIndex}
            setActiveLineItemIndex={setActiveLineItemIndex}
            setLineItemData={setLineItemData}
            setAllLineItemData={setAllLineItemData}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            getRiskScore={getRiskScore}
            formatLineItemValue={formatLineItemValue}
            RiskScoreDisplay={RiskScoreDisplay}
            lineItemNotes={lineItemNotes}
            setLineItemNotes={setLineItemNotes}
            showAddLineItemForm={showAddLineItemForm}
            setShowAddLineItemForm={setShowAddLineItemForm}
            newLineItem={newLineItem}
            setNewLineItem={setNewLineItem}
            handleAddLineItem={handleAddLineItem}
          />
        );
      case 6:
        return (
          <SOXCycleMapping
            lineItemData={lineItemData}
            riskScores={riskScores}
            setCurrentStep={setCurrentStep}
            getSoxCycles={getSoxCycles}
            toggleSection={toggleSection}
            expandedSections={expandedSections}
            renderLineItemWithSoxEdit={renderLineItemWithSoxEdit}
            allLineItemData={allLineItemData}
            activeLineItemIndex={activeLineItemIndex}
            customSoxCycles={customSoxCycles}
            soxDefaultsLoading={soxDefaultsLoading}
            soxDefaultsError={soxDefaultsError}
            onRetrySoxDefaults={retrySoxDefaults}
          />
        );
      case 7:
        return (
          <EntityScoping
            confirmedMateriality={confirmedMateriality}
            showScopingMatrix={showScopingMatrix}
            setShowScopingMatrix={setShowScopingMatrix}
            scopingFilter={scopingFilter}
            setScopingFilter={setScopingFilter}
            thresholdOverride={thresholdOverride}
            setThresholdOverride={setThresholdOverride}
            thresholdJustification={thresholdJustification}
            setThresholdJustification={setThresholdJustification}
            showThresholdOverride={showThresholdOverride}
            setShowThresholdOverride={setShowThresholdOverride}
            expandedGLCategories={expandedGLCategories}
            setExpandedGLCategories={setExpandedGLCategories}
            trialBalanceFile={trialBalanceFile}
            setTrialBalanceFile={handleSetTrialBalanceFile}
            isUploadingTrialBalance={isUploadingTrialBalance}
            trialBalanceInputRef={trialBalanceInputRef}
            handleTrialBalanceUpload={handleTrialBalanceUpload}
            handleAnalyzeTrialBalance={handleAnalyzeTrialBalance}
            isAnalyzingTrialBalance={isAnalyzingTrialBalance}
            glEntriesData={glEntriesData}
            setCurrentStep={setCurrentStep}
            setShowVersionModal={setShowVersionModal}
            onScopingDataChange={setEntityScopingDataForSave}
            showScopingReport={showScopingReport}
            isLoadingEntities={isLoadingEntities}
            entityScopingData={entityScopingData}
            entityPeriod={entityPeriod}
            entitiesAnalyzeError={entitiesAnalyzeError}
            isEntityDataNotFound={entitiesAnalyzeError === ENTITY_DATA_NOT_FOUND_MESSAGE}
            handleGenerateScopingReport={handleGenerateScopingReport}
          />
        );
      case 8:
        return renderSuccessConfirmation();
      default:
        return renderFiscalYearStep();
    }
  };

  const renderFiscalYearStep = () => (
    <div className="bg-white rounded-xl shadow-sm p-8">
      <div className="max-w-md">
        <h2 className="text-2xl font-semibold mb-2">Select Fiscal Year</h2>
        <p className="text-gray-600 mb-6">
          Choose the fiscal year for this analysis before continuing.
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
          Fiscal year <span className="text-red-500">*</span>
        </label>
        <Select
          aria-label="Fiscal year"
          options={fiscalYearOptions}
          value={selectedAnalysisYear}
          onChange={setSelectedAnalysisYear}
        />
      </div>
      <div className="flex justify-end mt-8">
        <button
          type="button"
          onClick={() => setCurrentStep(2)}
          disabled={!selectedAnalysisYear}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue to Upload Financials
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const renderUploadStep = () => (
    <div className="bg-white rounded-xl shadow-sm p-8">
      <div className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Upload Financial Statements</h2>
          <p className="text-gray-600 max-w-[520px] leading-6">
            Upload multiple documents including 10-Q, 10-K, balance sheets, income statements, or forecasts
          </p>
        </div>
      </div>

      {uploadedFiles.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Drop your files here, or browse</p>
          <p className="text-sm text-gray-500 mb-6">Supported formats: .pdf, .xlsx, .csv (Multiple files allowed)</p>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Browse Files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.xlsx,.xls,.csv"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      ) : (
        <div>
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-2">Uploaded Files ({uploadedFiles.length}):</p>
            <div className="space-y-2">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium">{getUploadSlotLabel(file, index)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="ml-2 text-gray-500 hover:text-red-600"
                      title="Remove file"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={handleChangeFile}
              className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              + Add More Files
            </button>
          </div>

          {/* Hidden file input for adding more files */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.xlsx,.xls,.csv"
            multiple
            onChange={handleAddMoreFilesChange}
            className="hidden"
          />

          {uploadProgress > 0 && (
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Upload Progress</span>
                <span className="text-gray-900 font-medium">{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleProceedToDocumentAnalysis}
              disabled={isProcessing || !selectedAnalysisYear}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Analyze Documents
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const handleTrialBalanceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploadingTrialBalance(true);

    // Simulate upload delay for animation
    await new Promise(resolve => setTimeout(resolve, 1000));

    handleSetTrialBalanceFile(file);
    setIsUploadingTrialBalance(false);
  };

  const closeTrialBalanceDialog = () => {
    setTrialBalanceDialog((prev) => ({ ...prev, open: false }));
  };

  const closeEntityAnalyzeDialog = () => {
    setEntityAnalyzeDialog((prev) => ({ ...prev, open: false }));
  };

  const resetEntityScopingResults = useCallback(() => {
    setShowScopingMatrix(false);
    setShowScopingReport(false);
    setGlEntriesData(null);
    setEntityScopingData([]);
    setEntityPeriod('');
    setEntitiesAnalyzeError(null);
    setEntityAnalyzeDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const hasTrialBalanceFileChanged = useCallback((prevFile, nextFile) => {
    if (!prevFile && !nextFile) return false;
    if (!prevFile || !nextFile) return true;

    return (
      prevFile.name !== nextFile.name ||
      prevFile.size !== nextFile.size ||
      prevFile.lastModified !== nextFile.lastModified
    );
  }, []);

  const handleSetTrialBalanceFile = useCallback(
    (nextFile) => {
      if (hasTrialBalanceFileChanged(trialBalanceFile, nextFile)) {
        resetEntityScopingResults();
      }
      setTrialBalanceFile(nextFile);
    },
    [hasTrialBalanceFileChanged, resetEntityScopingResults, trialBalanceFile]
  );

  const resolveUserHash = useCallback(() => {
    const resolvedUserHash = localStorage.getItem('userHash') || user?.user_hash || '';
    if (resolvedUserHash) {
      localStorage.setItem('userHash', resolvedUserHash);
    }
    return resolvedUserHash;
  }, [user?.user_hash]);

  /** Same UX as EntityScoping: alert when analyze-entities fails or returns no rows. */
  const entityAnalyzeModalEl = (
    <ConfirmDialogModal
      isOpen={entityAnalyzeDialog.open}
      onClose={closeEntityAnalyzeDialog}
      title={entityAnalyzeDialog.title}
      subtitle={entityAnalyzeDialog.subtitle || undefined}
      message={entityAnalyzeDialog.message}
      variant={entityAnalyzeDialog.variant}
      confirmLabel="OK"
      onConfirm={closeEntityAnalyzeDialog}
    />
  );

  // Drives the unified outer footer's "Generate Scoping Report" action.
  // Lifted out of EntityScoping so a single footer can trigger this flow
  // (and the modal/error UI lives at the page level).
  const handleGenerateScopingReport = async () => {
    if (!trialBalanceFile) return;

    setIsLoadingEntities(true);
    setEntitiesAnalyzeError(null);

    try {
      const userHash = resolveUserHash();
      const initResult = await initUpload(
        trialBalanceFile,
        userHash,
        MODULE_UPLOAD.ENTITY_SCOPING
      );
      await uploadToGCS(initResult.upload_url, trialBalanceFile, initResult.content_type);
      const completeResult = await completeUpload(
        initResult.file_ref,
        trialBalanceFile.name,
        trialBalanceFile.type || initResult.content_type || 'application/octet-stream',
        trialBalanceFile.size,
        userHash,
        MODULE_UPLOAD.ENTITY_SCOPING
      );
      const fileId = completeResult.uuid;
      if (!fileId) {
        throw new Error('Upload finished but no file id was returned. Cannot analyze entities.');
      }

      const response = await axiosInstance.post(
        '/api/entity-scoping/analyze-entities',
        { file_id: fileId, userHash },
        {
          headers: { 'Content-Type': 'application/json', Authorization: userHash },
          timeout: 240000,
        }
      );
      const data = response?.data || {};
      console.log('Entity Scoping Response:', data);

      const entities = Array.isArray(data?.entities) ? data.entities : [];
      const hasEntityRows = entities.length > 0;

      if (data?.success === true || hasEntityRows) {
        setEntityScopingData(entities);
        setEntityPeriod(data.period || '');
        setShowScopingReport(true);
        return;
      }

      setEntityScopingData([]);
      setShowScopingReport(false);
      setEntitiesAnalyzeError(ENTITY_DATA_NOT_FOUND_MESSAGE);
      setEntityAnalyzeDialog({
        open: true,
        title: 'Data not found',
        subtitle: 'Entity scoping',
        message: ENTITY_DATA_NOT_FOUND_MESSAGE,
        variant: 'warning',
      });
    } catch (error) {
      console.error('Error analyzing entities:', error);
      const errMsg = resolve403ErrorMessage(
        error,
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        error?.message ||
        'Something went wrong. Please try again.'
      );
      setEntityScopingData([]);
      setShowScopingReport(false);
      setEntitiesAnalyzeError(errMsg);
      setEntityAnalyzeDialog({
        open: true,
        title: 'Something went wrong',
        subtitle: 'Entity analysis',
        message: errMsg,
        variant: 'danger',
      });
    } finally {
      setIsLoadingEntities(false);
    }
  };

  const handleAnalyzeTrialBalance = async () => {
    if (!trialBalanceFile) {
      setTrialBalanceDialog({
        open: true,
        title: 'Trial balance required',
        subtitle: 'Entity scoping',
        message: 'Please upload a trial balance file first.',
        variant: 'warning',
      });
      return;
    }

    setIsAnalyzingTrialBalance(true);
    setShowScopingMatrix(true);

    try {
      const userHash = resolveUserHash();
      // Use common upload flow: init → uploadToGCS → complete (ENTITY_SCOPING)
      const initResult = await initUpload(trialBalanceFile, userHash, MODULE_UPLOAD.ENTITY_SCOPING);
      await uploadToGCS(initResult.upload_url, trialBalanceFile, initResult.content_type);
      const completeResult = await completeUpload(
        initResult.file_ref,
        trialBalanceFile.name,
        trialBalanceFile.type || initResult.content_type || 'application/octet-stream',
        trialBalanceFile.size,
        userHash,
        MODULE_UPLOAD.ENTITY_SCOPING
      );
      const fileId = completeResult.uuid;
      if (!fileId) {
        throw new Error('Upload finished but no file id was returned. Cannot analyze trial balance.');
      }

      const response = await axiosInstance.post(
        '/api/entity-scoping/analyze-trial-balance',
        { file_id: fileId, userHash },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: userHash,
          },
          timeout: 120000,
        }
      );

      console.log('Trial balance analysis response:', response.data);
      setGlEntriesData(response.data);
    } catch (error) {
      console.error('Error analyzing trial balance:', error);
      const errMsg = resolve403ErrorMessage(
        error,
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        error?.message ||
        'Error analyzing trial balance. Please try again.'
      );
      setTrialBalanceDialog({
        open: true,
        title: 'Trial balance analysis failed',
        subtitle: 'Could not process file',
        message: errMsg,
        variant: 'danger',
      });
      setShowScopingMatrix(false);
    } finally {
      setIsAnalyzingTrialBalance(false);
    }
  };

  // Function to export assessment to PDF
  const handleExportToPDF = async () => {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;

      // Helper function to format currency
      const formatCurrency = (value) => {
        if (!value) return '$0';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);
      };

      const formatLineItemValue = (value) => {
        if (value === null || value === undefined) return '$0';
        const isNegative = value < 0;
        const absValue = Math.abs(value);
        const formatted = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(absValue);
        return isNegative ? `-$${formatted}` : `$${formatted}`;
      };

      // Helper function to get risk label
      const getRiskLabel = (risk) => {
        if (!risk || risk === 0) return 'Not Assessed';
        const score = parseFloat(risk);
        if (score <= 3.3) return 'LOW';
        if (score <= 6.6) return 'MODERATE';
        return 'HIGH';
      };

      // Same risk cell colors as Assessment Reports PDF export
      const getPdfRiskCellStyles = (riskLevel) => {
        const u = String(riskLevel || '').toUpperCase();
        if (u === 'LOW') {
          return { textColor: [22, 101, 52], fillColor: [220, 252, 231] };
        }
        if (u === 'MODERATE') {
          return { textColor: [133, 77, 14], fillColor: [254, 249, 195] };
        }
        if (u === 'HIGH') {
          return { textColor: [153, 27, 27], fillColor: [254, 226, 226] };
        }
        return { textColor: [55, 65, 81], fillColor: [243, 244, 246] };
      };

      const resolveItemRiskScore = (item, idx, statementKey) => {
        let riskScore = item.risk_score || item.riskScore;
        if (!riskScore && riskScores) {
          const docRisk = getRiskScoresForActiveDocument(
            riskScores,
            activeLineItemIndex,
            allLineItemData.length > 1
          );
          const scoreData = docRisk?.[statementKey];
          if (scoreData?.items && scoreData.items[idx]) {
            riskScore = scoreData.items[idx].risk_score || scoreData.items[idx].riskScore;
          }
        }
        return riskScore;
      };

      // Cover Page
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text(versionName || 'Materiality Assessment', pageWidth / 2, 50, { align: 'center' });

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Financial Statement Risk Assessment', pageWidth / 2, 65, { align: 'center' });
      pdf.text('& SOX Cycle Mapping Report', pageWidth / 2, 73, { align: 'center' });

      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 90, { align: 'center' });

      if (companyProfile?.companyName) {
        pdf.text(`Company: ${companyProfile.companyName}`, pageWidth / 2, 100, { align: 'center' });
      }

      // ========================================
      // SECTION 1: MATERIALITY
      // ========================================
      pdf.addPage();
      let yPos = 20;

      // Section Title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(55, 65, 81);
      pdf.text('SECTION 1: MATERIALITY', margin, yPos);
      yPos += 10;

      // Horizontal line
      pdf.setDrawColor(209, 213, 219);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      // Company Profile
      if (companyProfile) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(17, 24, 39);
        pdf.text('Company Profile', margin, yPos);
        yPos += 8;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(75, 85, 99);

        pdf.setFont('helvetica', 'bold');
        pdf.text('Company Name:', margin + 5, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(companyProfile.companyName || 'N/A', margin + 50, yPos);
        yPos += 6;

        pdf.setFont('helvetica', 'bold');
        pdf.text('Industry:', margin + 5, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(companyProfile.industry || 'N/A', margin + 50, yPos);
        yPos += 6;

        pdf.setFont('helvetica', 'bold');
        pdf.text('Growth Stage:', margin + 5, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(companyProfile.growthStage || 'N/A', margin + 50, yPos);
        yPos += 12;

        // Financial Metrics
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(17, 24, 39);
        pdf.text('Financial Metrics', margin, yPos);
        yPos += 8;

        pdf.setFontSize(10);
        pdf.setTextColor(75, 85, 99);

        // Create metrics boxes
        const boxWidth = (pageWidth - 2 * margin - 10) / 3;
        const boxHeight = 25;
        const boxY = yPos;

        // Total Revenue Box
        pdf.setFillColor(238, 242, 255);
        pdf.roundedRect(margin, boxY, boxWidth, boxHeight, 2, 2, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.text('Total Revenue', margin + 3, boxY + 5);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text(formatCurrency(companyProfile.totalRevenue), margin + 3, boxY + 13);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.text(`Source: ${companyProfile.revenueSource || 'N/A'}`, margin + 3, boxY + 20);

        // Total Assets Box
        pdf.setFillColor(238, 242, 255);
        pdf.roundedRect(margin + boxWidth + 5, boxY, boxWidth, boxHeight, 2, 2, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.text('Total Assets', margin + boxWidth + 8, boxY + 5);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text(formatCurrency(companyProfile.totalAssets), margin + boxWidth + 8, boxY + 13);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.text(`Source: ${companyProfile.assetsSource || 'N/A'}`, margin + boxWidth + 8, boxY + 20);

        // Net Income Box
        const netIncomeColor = companyProfile.netIncome < 0 ? [220, 38, 38] : [34, 197, 94];
        pdf.setFillColor(238, 242, 255);
        pdf.roundedRect(margin + 2 * (boxWidth + 5), boxY, boxWidth, boxHeight, 2, 2, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.text('Net Income', margin + 2 * (boxWidth + 5) + 3, boxY + 5);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(...netIncomeColor);
        pdf.text(formatCurrency(companyProfile.netIncome), margin + 2 * (boxWidth + 5) + 3, boxY + 13);
        pdf.setTextColor(75, 85, 99);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.text(`Source: ${companyProfile.netIncomeSource || 'N/A'}`, margin + 2 * (boxWidth + 5) + 3, boxY + 20);

        yPos = boxY + boxHeight + 12;
      }

      // Materiality Calculation
      if (confirmedMateriality) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(17, 24, 39);
        pdf.text('Materiality Calculation', margin, yPos);
        yPos += 8;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(75, 85, 99);

        pdf.setFont('helvetica', 'bold');
        pdf.text('Selected Category:', margin + 5, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(selectedCategory || 'N/A', margin + 50, yPos);
        yPos += 6;

        pdf.setFont('helvetica', 'bold');
        pdf.text('Sensitivity Level:', margin + 5, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(sensitivityLevel || 'N/A', margin + 50, yPos);
        yPos += 10;

        // Materiality Thresholds
        const thresholdBoxWidth = (pageWidth - 2 * margin - 10) / 3;
        const thresholdBoxHeight = 20;
        const thresholdY = yPos;

        // Overall Materiality
        pdf.setFillColor(238, 242, 255);
        pdf.roundedRect(margin, thresholdY, thresholdBoxWidth, thresholdBoxHeight, 2, 2, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.text('Overall Materiality', margin + 3, thresholdY + 5);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.text(formatCurrency(confirmedMateriality.overallMateriality), margin + 3, thresholdY + 13);

        // Planning Materiality
        pdf.setFillColor(243, 232, 255);
        pdf.roundedRect(margin + thresholdBoxWidth + 5, thresholdY, thresholdBoxWidth, thresholdBoxHeight, 2, 2, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.text('Planning Materiality', margin + thresholdBoxWidth + 8, thresholdY + 5);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.text(formatCurrency(confirmedMateriality.planningMateriality), margin + thresholdBoxWidth + 8, thresholdY + 13);

        // De Minimis
        pdf.setFillColor(252, 231, 243);
        pdf.roundedRect(margin + 2 * (thresholdBoxWidth + 5), thresholdY, thresholdBoxWidth, thresholdBoxHeight, 2, 2, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.text('De Minimis', margin + 2 * (thresholdBoxWidth + 5) + 3, thresholdY + 5);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.text(formatCurrency(confirmedMateriality.deminimis), margin + 2 * (thresholdBoxWidth + 5) + 3, thresholdY + 13);

        yPos = thresholdY + thresholdBoxHeight + 10;

        // Materiality Adjustments
        if (overrideAmount || overrideReasoning) {
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(17, 24, 39);
          pdf.text('Materiality Adjustments', margin, yPos);
          yPos += 6;

          pdf.setFillColor(254, 252, 232);
          pdf.setDrawColor(254, 240, 138);
          pdf.setLineWidth(0.5);
          const adjustmentBoxHeight = overrideReasoning ? 20 : 12;
          pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, adjustmentBoxHeight, 2, 2, 'FD');

          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(75, 85, 99);

          if (overrideAmount) {
            pdf.setFont('helvetica', 'bold');
            pdf.text(`Override Amount: ${formatCurrency(parseFloat(overrideAmount))}`, margin + 3, yPos + 5);
            yPos += 6;
          }
          if (overrideReasoning) {
            pdf.setFont('helvetica', 'normal');
            const reasoningLines = pdf.splitTextToSize(`Reasoning: ${overrideReasoning}`, pageWidth - 2 * margin - 6);
            pdf.text(reasoningLines, margin + 3, yPos + 5);
            yPos += reasoningLines.length * 4;
          }
          yPos += adjustmentBoxHeight - (overrideAmount ? 6 : 0);
        }
      }

      // Source Documents
      if (documentInfo?.documents && documentInfo.documents.length > 0) {
        yPos += 5;
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          yPos = 20;
        }

        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(17, 24, 39);
        pdf.text('Source Documents', margin, yPos);
        yPos += 8;

        documentInfo.documents.forEach((doc, idx) => {
          if (yPos > pageHeight - 30) {
            pdf.addPage();
            yPos = 20;
          }

          pdf.setFillColor(249, 250, 251);
          pdf.setDrawColor(229, 231, 235);
          pdf.setLineWidth(0.3);
          pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 20, 2, 2, 'FD');

          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(17, 24, 39);
          pdf.text(doc.sourceFile || `Document ${idx + 1}`, margin + 3, yPos + 5);

          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(75, 85, 99);
          pdf.text(`${doc.companyName || 'N/A'} • ${doc.documentType || 'N/A'}`, margin + 3, yPos + 10);
          pdf.text(`Period: ${doc.period || 'N/A'} (${doc.periodMonths || 'N/A'} months)`, margin + 3, yPos + 15);

          if (doc.annualizationFactor > 1) {
            pdf.setFillColor(219, 234, 254);
            pdf.setTextColor(30, 58, 138);
            pdf.setFontSize(8);
            pdf.roundedRect(pageWidth - margin - 40, yPos + 2, 38, 6, 1, 1, 'F');
            pdf.text(`Annualized (×${doc.annualizationFactor})`, pageWidth - margin - 38, yPos + 6);
          }

          yPos += 24;
        });
      }

      // ========================================
      // SECTION 2: SOX CYCLES
      // ========================================
      pdf.addPage();
      yPos = 20;

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(55, 65, 81);
      pdf.text('SECTION 2: SOX CYCLES', margin, yPos);
      yPos += 10;

      pdf.setDrawColor(209, 213, 219);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      // Collect unique SOX cycles
      const uniqueSoxCycles = new Set();
      const cycleFullNames = {
        'ELC': 'Entity Level Controls',
        'FSC': 'Financial Statement Close',
        'ER': 'External Reporting',
        'OTC': 'Order to Cash',
        'TRS': 'Treasury',
        'DBT': 'Debt',
        'PTP': 'Procure to Pay',
        'HTR': 'Hire to Retire',
        'INV': 'Inventory',
        'TAX': 'Tax',
        'FIX': 'Fixed Assets',
        'LEA': 'Leases',
        'EQY': 'Equity',
        'PTR': 'Procure to Retire',
        'RCM': 'Record to Maintain',
        'STK': 'Stock Compensation',
        'FLC': 'Fixed Asset / Liability Cycle',
        'EXT': 'External Investments'
      };

      // Add custom cycle names
      if (customSoxCycles) {
        customSoxCycles.forEach(cycle => {
          cycleFullNames[cycle.code] = cycle.name;
        });
      }

      // Collect cycles from line items
      if (lineItemData) {
        // From balance sheet
        if (lineItemData.balance_sheet?.items) {
          lineItemData.balance_sheet.items.forEach((item) => {
            const soxCycles = item.sox_cycles || getSoxCycles(item.label, item, 'balance_sheet');
            soxCycles.forEach(cycle => uniqueSoxCycles.add(cycle));
          });
        }
        // From income statement
        if (lineItemData.income_statement?.items) {
          lineItemData.income_statement.items.forEach((item) => {
            const soxCycles = item.sox_cycles || getSoxCycles(item.label, item, 'income_statement');
            soxCycles.forEach(cycle => uniqueSoxCycles.add(cycle));
          });
        }
      }

      const sortedCycles = Array.from(uniqueSoxCycles).sort();

      // Unique SOX Cycles Summary
      pdf.setFillColor(239, 246, 255);
      pdf.setDrawColor(191, 219, 254);
      pdf.setLineWidth(0.5);
      pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 12 + Math.ceil(sortedCycles.length / 5) * 6, 2, 2, 'FD');

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 64, 175);
      pdf.text(`Unique SOX Cycles Mapped: ${sortedCycles.length} cycle${sortedCycles.length !== 1 ? 's' : ''}`, margin + 3, yPos + 6);

      yPos += 10;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      let xPos = margin + 3;
      sortedCycles.forEach((cycle, idx) => {
        const cycleName = cycleFullNames[cycle] || cycle;
        const cycleText = `${cycle} - ${cycleName}`;
        const textWidth = pdf.getTextWidth(cycleText);

        if (xPos + textWidth + 5 > pageWidth - margin) {
          xPos = margin + 3;
          yPos += 6;
        }

        pdf.text(cycleText, xPos, yPos);
        xPos += textWidth + 8;
      });

      yPos += 12;

      // Balance Sheet Line Items Table
      if (lineItemData?.balance_sheet?.items && lineItemData.balance_sheet.items.length > 0) {
        yPos += 5;
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          yPos = 20;
        }

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(17, 24, 39);
        pdf.text('Balance Sheet Line Items', margin, yPos);
        yPos += 8;

        const bsTableData = [];
        const bsRowMeta = [];
        const bsSections = [
          { key: 'asset', label: 'Assets' },
          { key: 'liability', label: 'Liabilities' },
          { key: 'equity', label: 'Equity' },
        ];
        bsSections.forEach(({ key, label }) => {
          const sectionRows = [];
          lineItemData.balance_sheet.items.forEach((item, idx) => {
            // Same as Assessment Reports: skip total/subtotal rows
            if (String(item?.label ?? '').toLowerCase().includes('total')) return;
            if (normalizeBalanceSheetItemCategory(item) !== key) return;
            sectionRows.push({ item, idx });
          });
          if (sectionRows.length === 0) return;
          bsTableData.push([label, '', '', '']);
          bsRowMeta.push({ type: 'section' });
          sectionRows.forEach(({ item, idx }) => {
            const soxCycles = item.sox_cycles || getSoxCycles(item.label, item, 'balance_sheet');
            const riskScore = resolveItemRiskScore(item, idx, 'balance_sheet');
            const riskLevel = getRiskLabel(riskScore);
            const riskLabel = riskScore ? `${riskLevel} (${Number(riskScore).toFixed(1)})` : 'Not Assessed';
            bsTableData.push([
              item.label,
              riskLabel,
              Array.isArray(soxCycles) ? soxCycles.join(', ') : '',
              formatLineItemValue(item.value)
            ]);
            bsRowMeta.push({ type: 'item', riskLevel });
          });
        });

        autoTable(pdf, {
          startY: yPos,
          head: [['Line Item', 'Risk Score', 'SOX Cycles', 'Amount']],
          body: bsTableData,
          theme: 'striped',
          headStyles: {
            fillColor: [99, 102, 241],
            textColor: [255, 255, 255],
            fontSize: 9,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontSize: 8,
            textColor: [55, 65, 81]
          },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 35 },
            2: { cellWidth: 50 },
            3: { cellWidth: 35, halign: 'right' }
          },
          margin: { left: margin, right: margin },
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            const meta = bsRowMeta[data.row.index];
            if (!meta) return;
            if (meta.type === 'section') {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [243, 244, 246];
              data.cell.styles.textColor = [17, 24, 39];
              return;
            }
            if (data.column.index !== 1) return;
            const styles = getPdfRiskCellStyles(meta.riskLevel);
            data.cell.styles.textColor = styles.textColor;
            data.cell.styles.fillColor = styles.fillColor;
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.halign = 'center';
          },
        });

        yPos = pdf.lastAutoTable.finalY + 10;
      }

      // Income Statement Line Items Table
      if (lineItemData?.income_statement?.items && lineItemData.income_statement.items.length > 0) {
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          yPos = 20;
        }

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(17, 24, 39);
        pdf.text('Income Statement Line Items', margin, yPos);
        yPos += 8;

        const isTableData = [];
        const isRowMeta = [];
        const isSections = [
          { key: 'revenue', label: 'Revenue' },
          { key: 'expense', label: 'Expenses' },
        ];
        isSections.forEach(({ key, label }) => {
          const sectionRows = [];
          lineItemData.income_statement.items.forEach((item, idx) => {
            // Same as Assessment Reports: skip total/subtotal rows
            if (String(item?.label ?? '').toLowerCase().includes('total')) return;
            if (normalizeIncomeStatementItemCategory(item) !== key) return;
            sectionRows.push({ item, idx });
          });
          if (sectionRows.length === 0) return;
          isTableData.push([label, '', '', '']);
          isRowMeta.push({ type: 'section' });
          sectionRows.forEach(({ item, idx }) => {
            const soxCycles = item.sox_cycles || getSoxCycles(item.label, item, 'income_statement');
            const riskScore = resolveItemRiskScore(item, idx, 'income_statement');
            const riskLevel = getRiskLabel(riskScore);
            const riskLabel = riskScore ? `${riskLevel} (${Number(riskScore).toFixed(1)})` : 'Not Assessed';
            isTableData.push([
              item.label,
              riskLabel,
              Array.isArray(soxCycles) ? soxCycles.join(', ') : '',
              formatLineItemValue(item.value)
            ]);
            isRowMeta.push({ type: 'item', riskLevel });
          });
        });

        autoTable(pdf, {
          startY: yPos,
          head: [['Line Item', 'Risk Score', 'SOX Cycles', 'Amount']],
          body: isTableData,
          theme: 'striped',
          headStyles: {
            fillColor: [34, 197, 94],
            textColor: [255, 255, 255],
            fontSize: 9,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontSize: 8,
            textColor: [55, 65, 81]
          },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 35 },
            2: { cellWidth: 50 },
            3: { cellWidth: 35, halign: 'right' }
          },
          margin: { left: margin, right: margin },
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            const meta = isRowMeta[data.row.index];
            if (!meta) return;
            if (meta.type === 'section') {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [243, 244, 246];
              data.cell.styles.textColor = [17, 24, 39];
              return;
            }
            if (data.column.index !== 1) return;
            const styles = getPdfRiskCellStyles(meta.riskLevel);
            data.cell.styles.textColor = styles.textColor;
            data.cell.styles.fillColor = styles.fillColor;
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.halign = 'center';
          },
        });
      }

      // ========================================
      // SECTION 3: ENTITY SCOPING (Section 1 Only)
      // ========================================
      if (entityScopingDataForSave && entityScopingDataForSave.entityScopingData &&
          entityScopingDataForSave.entityScopingData.length > 0) {
        pdf.addPage();
        yPos = 20;

        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(55, 65, 81);
        pdf.text('SECTION 3: ENTITY SCOPING', margin, yPos);
        yPos += 10;

        pdf.setDrawColor(209, 213, 219);
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(17, 24, 39);
        pdf.text('Section 1: Entity Scoping Analysis', margin, yPos);
        yPos += 8;

        // Materiality Thresholds
        if (entityScopingDataForSave.editableMateriality) {
          pdf.setFillColor(239, 246, 255);
          pdf.setDrawColor(191, 219, 254);
          pdf.setLineWidth(0.5);
          pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 20, 2, 2, 'FD');

          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(30, 64, 175);
          pdf.text('Materiality Thresholds', margin + 3, yPos + 6);

          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(55, 65, 81);

          const om = entityScopingDataForSave.editableMateriality.overallMateriality ||
                     entityScopingDataForSave.editableMateriality;
          const pm = entityScopingDataForSave.editableMateriality.planningMateriality || 0;
          const dm = entityScopingDataForSave.editableMateriality.deminimis || 0;

          pdf.text(`Overall Materiality: ${formatCurrency(om)}`, margin + 3, yPos + 12);
          pdf.text(`Planning Materiality: ${formatCurrency(pm)}`, margin + 70, yPos + 12);
          pdf.text(`Sum De Minimis: ${formatCurrency(dm)}`, margin + 140, yPos + 12);

          yPos += 25;
        }

        // Location Scoping Table
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(75, 85, 99);
        pdf.text('Review materiality assessment by location:', margin, yPos);
        yPos += 8;

        const entityTableData = [];
        entityScopingDataForSave.entityScopingData.forEach(row => {
          const quantConclusion = entityScopingDataForSave.entityConclusions?.[row.entity] ||
                                  row.conclusion || 'Not Assessed';
          const qualComment = entityScopingDataForSave.qualitativeComments?.[row.entity] || 'No assessment';
          const finalConclusion = entityScopingDataForSave.finalConclusions?.[row.entity] || quantConclusion;

          entityTableData.push([
            row.entity,
            `$${row.revenue.toLocaleString()}`,
            `$${row.costs.toLocaleString()}`,
            `${row.revPct}%`,
            `${row.costPct}%`,
            quantConclusion,
            qualComment.length > 40 ? qualComment.substring(0, 37) + '...' : qualComment,
            finalConclusion
          ]);
        });

        autoTable(pdf, {
          startY: yPos,
          head: [['Entity', 'Revenue', 'Costs', '% Rev', '% Cost', 'Quant. Conclusion', 'Qualitative Assessment', 'Final Conclusion']],
          body: entityTableData,
          theme: 'grid',
          headStyles: {
            fillColor: [249, 250, 251],
            textColor: [55, 65, 81],
            fontSize: 7,
            fontStyle: 'bold',
            lineWidth: 0.3,
            lineColor: [209, 213, 219]
          },
          bodyStyles: {
            fontSize: 7,
            textColor: [55, 65, 81],
            lineWidth: 0.1,
            lineColor: [229, 231, 235]
          },
          columnStyles: {
            0: { cellWidth: 25, fontStyle: 'bold' },
            1: { cellWidth: 20, halign: 'right' },
            2: { cellWidth: 20, halign: 'right' },
            3: { cellWidth: 12, halign: 'right' },
            4: { cellWidth: 12, halign: 'right' },
            5: { cellWidth: 22, halign: 'center' },
            6: { cellWidth: 35 },
            7: { cellWidth: 22, halign: 'center' }
          },
          margin: { left: margin, right: margin },
          didParseCell: function(data) {
            // Color code conclusions
            if (data.column.index === 5 || data.column.index === 7) {
              const cellText = data.cell.text[0];
              if (cellText === 'In-Scope') {
                data.cell.styles.fillColor = data.column.index === 5 ? [219, 234, 254] : [220, 252, 231];
                data.cell.styles.textColor = data.column.index === 5 ? [30, 64, 175] : [21, 128, 61];
              } else if (cellText === 'Out-of-Scope') {
                data.cell.styles.fillColor = [254, 226, 226];
                data.cell.styles.textColor = [153, 27, 27];
              }
            }
          }
        });
      }

      // Save the PDF
      const fileName = `${(versionName || 'Materiality_Assessment').replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const renderSuccessConfirmation = () => {
    // Add inline styles for animations
    const animationStyles = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes scaleIn {
        from {
          opacity: 0;
          transform: scale(0.5);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      @keyframes checkMark {
        from {
          stroke-dashoffset: 100;
        }
        to {
          stroke-dashoffset: 0;
        }
      }
      .animate-fade-in-up {
        animation: fadeInUp 0.6s ease-out forwards;
        opacity: 0;
      }
      .animate-scale-in {
        animation: scaleIn 0.5s ease-out forwards;
      }
      .animation-delay-200 { animation-delay: 0.2s; }
      .animation-delay-300 { animation-delay: 0.3s; }
      .animation-delay-400 { animation-delay: 0.4s; }
      .animation-delay-500 { animation-delay: 0.5s; }
      .animation-delay-600 { animation-delay: 0.6s; }
      .animation-delay-700 { animation-delay: 0.7s; }
      .animation-delay-800 { animation-delay: 0.8s; }
      .animation-delay-900 { animation-delay: 0.9s; }
    `;

    return (
      <>
        <style>{animationStyles}</style>
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="max-w-2xl mx-auto text-center">
            {/* Success Icon with animation */}
            <div className="mb-6 flex justify-center animate-fade-in-up">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-scale-in">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
            </div>
            
            {/* Success Message with animation */}
            <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up animation-delay-200">Assessment Complete!</h2>
            <p className="text-lg text-gray-600 mb-8 animate-fade-in-up animation-delay-300">
              Your financial statement risk assessment and SOX cycle mapping has been successfully completed.
            </p>
            
            
            
            {/* Key Information with animation */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 text-left animate-fade-in-up animation-delay-800">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">What's Next?</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Your assessment has been saved and is ready for review</li>
                <li>• View the complete SOX mapping report with detailed risk analysis</li>
                <li>• Export your assessment data for documentation purposes</li>
                <li>• Schedule SOX Walkthroughs for in scope SOX cycles</li>
              </ul>
            </div>
          </div>
            </div>
            
            {/* Action Buttons with animation */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up animation-delay-900">
          <button
            onClick={() => setCurrentStep(6)}
            className="w-full sm:w-48 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={handleExportToPDF}
            className="w-full sm:w-48 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <FileDown className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => {
              // Save assessment data to localStorage for the report
              const assessmentData = {
                lineItemData,
                riskScores,
                documentInfo,
                companyProfile,
                confirmedMateriality,
                assessmentDate: new Date().toISOString()
              };
              localStorage.setItem('soxAssessmentData', JSON.stringify(assessmentData));

              // Navigate to SOX mapping report
              window.location.href = '/sox-mapping-report';
            }}
            className="w-full sm:w-48 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            View Report
            <ArrowRight className="w-4 h-4" />
          </button>
            </div>

            {/* Start New Assessment Button */}
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to start a new assessment? This will clear all saved progress.')) {
                    // Clear saved state
                    localStorage.removeItem(MATERIALITY_ASSESSMENT_STORAGE_KEY);
                    localStorage.removeItem('assessmentVersionName');
                    localStorage.removeItem('soxAssessmentData');

                    // Reset to step 1
                    window.location.reload();
                  }
                }}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Start New Assessment
              </button>
            </div>
          </div>
        </div>

        {/* Modal is now rendered at root level */}
      </>
    );
  };

  // Determine if header should be shown - hide once files are uploaded
  const showHeader = uploadedFiles.length === 0 && currentStep !== 8;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with smooth animation */}
      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
        showHeader ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-2xl font-bold text-gray-900">Risk Assessment and Scoping Process</h1>
            <p className="text-gray-600 mt-1">Complete the 7-step process to perform comprehensive risk assessment and SOX cycle mapping</p>
          </div>
        </div>
      </div>

      {/* Progress Bar - Hidden on Step 8 */}
      {currentStep !== 8 && (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Step {currentStep} of 7</span>
              <span className="text-sm font-medium text-gray-900">
                {steps[currentStep - 1]?.name}
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-500"
                style={{ width: `${(currentStep / 7) * 100}%` }}
              />
            </div>

            {/* Step Pills */}
            <div className="flex items-center justify-center mt-6 space-x-2">
              {steps.filter(step => !step.hidden).map((step) => (
                <button
                  key={step.id}
                  onClick={() => {
                    if (step.id <= currentStep || (step.id === 2 && !!selectedAnalysisYear) || (step.id === 3 && uploadedFiles.length > 0)) {
                      setCurrentStep(step.id);
                    }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    step.id === currentStep
                      ? 'bg-blue-600 text-white'
                      : step.id < currentStep
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  disabled={step.id > currentStep && !(step.id === 2 && !!selectedAnalysisYear) && !(step.id === 3 && uploadedFiles.length > 0)}
                >
                  {step.id < currentStep ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <step.icon className="w-4 h-4" />
                  )}
                  {step.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Suspense
          fallback={
            <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-600">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
              <span className="text-sm">Loading step…</span>
            </div>
          }
        >
          {renderStepContent()}
        </Suspense>
      </div>

      {/* Risk Score Edit Modal - Always rendered at root level */}
      <RiskScoreEditModal />

      {/* Navigation */}
      {currentStep > 1 && currentStep < 8 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="flex items-center justify-between">
            {/* Left side: Previous and Restart buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  // On step 7, collapse the scoping report back to the
                  // upload view before leaving the step.
                  if (currentStep === 7 && showScopingReport) {
                    setShowScopingReport(false);
                    return;
                  }
                  setCurrentStep((s) => s - 1);
                }}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous Step
              </button>
              {currentStep > 1 && (
                <button
                  onClick={handleRestartFlow}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  title="Restart entire flow from beginning"
                >
                  <RotateCcw className="w-4 h-4" />
                  Restart Flow
                </button>
              )}
            </div>

            {/* Right side: Progress buttons */}
            <div>
            {currentStep === 3 && (
              <button
                onClick={handleProceedToMaterialityAnalysis}
                disabled={
                  selectedDocumentIndices.length === 0 ||
                  !riskAssessmentDocIndices ||
                  riskAssessmentDocIndices.length === 0 ||
                  !selectedAnalysisYear
                }
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  selectedDocumentIndices.length === 0 ||
                  !riskAssessmentDocIndices ||
                  riskAssessmentDocIndices.length === 0 ||
                  !selectedAnalysisYear
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Proceed to Materiality Analysis
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {currentStep === 4 && companyProfile && (
              <button
                onClick={() => {
                  // Validate reasoning if any custom materiality values have been edited
                  if (showReasoningBox && !overrideReasoning.trim()) {
                    toast.error('Please provide reasoning for your materiality adjustments');
                    return;
                  }

                  // Calculate final materiality values, using custom values if set
                  let finalOverallMateriality = calculateMateriality();
                  if (customOverallMateriality && customOverallMateriality.toString().trim() !== '') {
                    const parsed = parseFloat(customOverallMateriality.toString().replace(/,/g, ''));
                    if (!isNaN(parsed)) {
                      finalOverallMateriality = parsed;
                    }
                  }

                  let finalPlanningMateriality = finalOverallMateriality * 0.5;
                  if (customPlanningMateriality && customPlanningMateriality.toString().trim() !== '') {
                    const parsed = parseFloat(customPlanningMateriality.toString().replace(/,/g, ''));
                    if (!isNaN(parsed)) {
                      finalPlanningMateriality = parsed;
                    }
                  }

                  let finalDeminimis = finalPlanningMateriality * 0.1;
                  if (customDeminimis && customDeminimis.toString().trim() !== '') {
                    const parsed = parseFloat(customDeminimis.toString().replace(/,/g, ''));
                    if (!isNaN(parsed)) {
                      finalDeminimis = parsed;
                    }
                  }

                  // Save the confirmed materiality values with user adjustments
                  const confirmedValues = {
                    overallMateriality: finalOverallMateriality,
                    planningMateriality: finalPlanningMateriality,
                    deminimis: finalDeminimis,
                    selectedCategory: selectedCategory,
                    sensitivityLevel: sensitivityLevel,
                    overrideAmount: overrideAmount,
                    overrideReasoning: overrideReasoning,
                    customOverallMateriality: customOverallMateriality,
                    customPlanningMateriality: customPlanningMateriality,
                    customDeminimis: customDeminimis,
                    customCategories: customCategories // Save custom categories for future reference
                  };
                  const previousConfirmed = confirmedMateriality;
                  const didMaterialityChange = !previousConfirmed || (
                    Number(previousConfirmed.overallMateriality) !== Number(confirmedValues.overallMateriality) ||
                    Number(previousConfirmed.planningMateriality) !== Number(confirmedValues.planningMateriality) ||
                    Number(previousConfirmed.deminimis) !== Number(confirmedValues.deminimis) ||
                    String(previousConfirmed.selectedCategory || '') !== String(confirmedValues.selectedCategory || '') ||
                    String(previousConfirmed.sensitivityLevel || '') !== String(confirmedValues.sensitivityLevel || '')
                  );

                  setConfirmedMateriality(confirmedValues);
                  if (didMaterialityChange) {
                    setRiskScores(null);
                  }
                  setCurrentStep(5);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Confirm Materiality
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {currentStep === 5 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleRiskAssessment}
                  disabled={
                    isCalculatingRisk ||
                    !riskAssessmentDocIndices ||
                    riskAssessmentDocIndices.length === 0 ||
                    riskScores !== null
                  }
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                    isCalculatingRisk ||
                    !riskAssessmentDocIndices ||
                    riskAssessmentDocIndices.length === 0 ||
                    riskScores !== null
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isCalculatingRisk ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Calculating...
                    </>
                  ) : riskScores ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Assessment Complete
                    </>
                  ) : (
                    <>
                      <Activity className="w-4 h-4" />
                      Perform Risk Assessment
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    if (!riskScores) {
                      toast.error('Please complete the risk assessment before proceeding to SOX Cycle Mapping');
                      return;
                    }
                    setCurrentStep(6);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                    riskScores
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  disabled={!riskScores}
                >
                  Continue to SOX Cycle Mapping
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
            {currentStep === 6 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowVersionModal(true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                    (soxDefaultsError || soxDefaultsLoading)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                  disabled={soxDefaultsError || soxDefaultsLoading}
                >
                  <CheckCircle className="w-4 h-4" />
                  Finish Risk and Scoping Assessment
                </button>
                <button
                  onClick={() => setCurrentStep(7)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                    (soxDefaultsError || soxDefaultsLoading)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  disabled={soxDefaultsError || soxDefaultsLoading}
                >
                  Continue to Entity Scoping
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
            {currentStep === 7 && !showScopingReport && (
              <button
                type="button"
                onClick={() => {
                  if (!trialBalanceFile) {
                    setTrialBalanceDialog({
                      open: true,
                      title: 'Trial balance required',
                      subtitle: 'Entity scoping',
                      message:
                        'Please upload a Trial Balance file before generating the scoping report.',
                      variant: 'warning',
                    });
                    return;
                  }
                  if (entityScopingData.length === 0) {
                    void handleGenerateScopingReport();
                    return;
                  }
                  setShowScopingReport(true);
                }}
                disabled={isLoadingEntities || !trialBalanceFile}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  isLoadingEntities || !trialBalanceFile
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isLoadingEntities ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Analyzing Entities...
                  </>
                ) : (
                  <>
                    {entityScopingData.length === 0
                      ? 'Generate Scoping Report'
                      : 'Open Scoping Report'}
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
            {currentStep === 7 && showScopingReport && (
              <button
                onClick={() => setShowVersionModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4" />
                Complete Risk Assessment
              </button>
            )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialogModal
        isOpen={trialBalanceDialog.open}
        onClose={closeTrialBalanceDialog}
        title={trialBalanceDialog.title}
        subtitle={trialBalanceDialog.subtitle || undefined}
        message={trialBalanceDialog.message}
        variant={trialBalanceDialog.variant}
        confirmLabel="OK"
        onConfirm={closeTrialBalanceDialog}
      />

      {entityAnalyzeModalEl}

      {/* Version Name Modal */}
      {showVersionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Save Assessment Version</h3>
            <p className="text-gray-600 mb-6">
              Please provide a version name for this materiality assessment. This will help you identify and track different versions of your assessments.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Version Name
              </label>
              <input
                type="text"
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                placeholder="e.g., Q4 2024 Assessment, FY2024 Final, etc."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && versionName.trim() && !isSavingAssessment) {
                    handleSaveAssessment();
                  }
                }}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowVersionModal(false);
                  setVersionName('');
                }}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAssessment}
                disabled={!versionName.trim() || isSavingAssessment}
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  versionName.trim() && !isSavingAssessment
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSavingAssessment ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save & Continue'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Materiality;